import { sha256 } from '@noble/hashes/sha256';
import { z } from 'zod';

// src/catalog/entityTypes.ts
var ENTITY_TYPES = [
  "bridge",
  "cefi",
  "company",
  "contract",
  "dao",
  "defi",
  "dex",
  "dex_pool",
  "exchange",
  "individual",
  "mixer",
  "nft",
  "sanctioned",
  "token",
  "wallet",
  "other"
];
var ENTITY_TYPE_LABEL = Object.freeze({
  bridge: "Bridge",
  cefi: "Centralised service",
  company: "Company",
  contract: "Contract",
  dao: "DAO",
  defi: "DeFi protocol",
  dex: "DEX",
  dex_pool: "DEX pool",
  exchange: "Exchange",
  individual: "Individual",
  mixer: "Mixer",
  nft: "NFT",
  sanctioned: "Sanctioned",
  token: "Token",
  wallet: "Wallet",
  other: "Other"
});
new Set(ENTITY_TYPES);

// src/funds/categories.ts
var NOTABLE_CATEGORIES = Object.freeze([
  "sanctioned",
  "mixer",
  "exchange",
  "cefi",
  "bridge"
]);
var ALARMING_CATEGORIES = Object.freeze([
  "mixer",
  "sanctioned"
]);
var UNKNOWN_CATEGORIES = Object.freeze([
  "unattributed",
  "resolving"
]);
var CATEGORY_LABEL = Object.freeze({
  resolving: "Still resolving\u2026",
  ...ENTITY_TYPE_LABEL,
  unattributed: "Unattributed",
  minted: "Minted",
  burned: "Burned"
});
function categoryLabel(category) {
  return CATEGORY_LABEL[category] ?? category;
}
var ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
function isZeroAddress(address) {
  return (address ?? "").toLowerCase() === ZERO_ADDRESS;
}

// src/funds/types.ts
function flowSelectionIds(flow, selection) {
  const buckets = flow?.[selection.direction === "in" ? "source" : "destination"].buckets ?? [];
  if (selection.counterparty) {
    return [...new Set(buckets.flatMap(
      (bucket) => bucket.top.filter((party) => party.address === selection.counterparty).flatMap((party) => party.transactionIds ?? [])
    ))];
  }
  return buckets.find((bucket) => bucket.category === selection.category)?.transactionIds ?? [];
}
var MAX_BUCKET_COUNTERPARTIES = 8;
var EMPTY_SIDE = Object.freeze({
  buckets: [],
  usdTotal: 0,
  unpricedCount: 0,
  transactions: 0
});

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  XRPL: "xrpl",
  TRON: "tron",
  NEAR: "near",
  OTHER: "other"
};
var EVM_IDENTIFIER_FAMILIES = /* @__PURE__ */ new Set([
  ChainFamily.EVM,
  ChainFamily.HYPERCORE
]);
function hasEvmIdentifiers(family) {
  return family != null && EVM_IDENTIFIER_FAMILIES.has(family);
}

// src/chains/seed-data.ts
var CHAIN_MAPPING = {
  // --- EVM mainnets ---
  "0x1": "eth",
  "0x3d": "etc",
  "0x89": "polygon",
  "0x38": "bsc",
  "0xcc": "opbnb",
  "0xa86a": "avalanche",
  "0xfa": "fantom",
  "0x19": "cronos",
  "0x2a15c308d": "palm",
  "0xa4b1": "arbitrum",
  "0x64": "gnosis",
  "0x15b38": "chiliz",
  "0x2105": "base",
  "0xa": "optimism",
  "0x2019": "klaytn",
  "0xa4ec": "celo",
  "0x8ae": "kava",
  "0x440": "metis",
  "0x63564c40": "harmony",
  "0x171": "pulsechain",
  "0x1388": "mantle",
  // Nesa. A Cosmos-SDK chain with an EVM module, so its Cosmos chain id
  // (`nesa_41443-1`) and its EVM chain id are the same 41443. Ported from
  // `_seed_data.py`, which has carried it since the Hyperlane domain fallback
  // was retired — without this row every spelling of Nesa falls through the
  // registry unresolved and renders as a raw `eip155:41443`.
  "0xa1e3": "nesa",
  "0x505": "moonriver",
  "0x504": "moonbeam",
  "0x144": "zkSync_era",
  "0x44d": "polygon_zkevm",
  "0xe708": "linea",
  "0x82750": "scroll",
  "0x13e31": "blast",
  "0x4e454152": "aurora",
  "0x7a": "fuse",
  "0x2eb": "flow",
  "0x7e4": "ronin",
  "0x46f": "lisk",
  "0x1b58": "zetachain",
  "0x531": "sei",
  // HyperEVM. Its L1 order book (HyperCore) is a non-EVM chain in registry.ts.
  "0x3e7": "hyperevm",
  // Ink, Kraken's OP Stack L2 — ported from `_seed_data.py` (CCIP + LayerZero route to it).
  "0xdef1": "ink",
  // Robinhood Chain (4663). Seeded alongside Ink because a Relay transfer
  // has a leg on each and, unseeded, the id resolved to nothing at all.
  "0x1237": "robinhood",
  "0x8f": "monad",
  "0x2611": "plasma",
  // peaq. Ported from `_seed_data.py`, where it was seeded because a LayerZero
  // OFT leg named its endpoint id and the chip rendered the bare number. The
  // lower-case name is the project's own styling.
  "0xd0a": "peaq",
  // GRVT, a ZK Stack chain. This id was seeded as `zkSync_era_testnet`, so
  // `eip155:325` resolved as a zkSync Era testnet paired with Era mainnet; Era's
  // testnets are 280 and 300. Ported from `_seed_data.py`, which carries the
  // sources for the id and for the native token (GBT, not ETH).
  "0x145": "grvt",
  // --- EVM testnets ---
  "0x5": "goerli",
  "0xaa36a7": "sepolia",
  "0x4268": "holesky",
  "0x13881": "mumbai",
  "0x13882": "polygon_amoy",
  "0x61": "bsc_testnet",
  "0x27d8": "gnosis_chiado",
  "0x15b32": "chiliz_spicy",
  "0x14a33": "base_goerli",
  "0x14a34": "base_sepolia",
  "0x1a4": "optimism_goerli",
  "0xaa37dc": "optimism_sepolia",
  "0x66eee": "arbitrum_sepolia",
  "0x3e9": "klaytn_baobab",
  "0xaef3": "celo_alfajores",
  "0x8ad": "kava_testnet",
  "0x24c": "metis_goerli",
  "0x6357d2e0": "harmony_testnet",
  "0x4e454153": "aurora_testnet",
  "0x507": "moonbase",
  "0x118": "zkSync_testnet",
  "0x12c": "zksync_sepolia",
  "0x7b": "fuse_testnet",
  "0xe704": "linea_testnet",
  "0xe705": "linea_sepolia",
  "0x8274f": "scroll_testnet",
  "0x138b": "mantle_sepolia",
  "0xa0c71fd": "blast_sepolia",
  "0x98a": "polygon_zkevm_cardona",
  "0xfa2": "fantom_testnet",
  "0x106a": "lisk_sepolia",
  "0x221": "flow_testnet",
  "0x7e5": "ronin_saigon",
  "0x1b59": "zetachain_testnet",
  "0x530": "sei_testnet",
  "0x3e6": "hyperevm_testnet"
};
var TESTNET_CHAINS = [
  "goerli",
  "sepolia",
  "mumbai",
  "bsc_testnet",
  "gnosis_chiado",
  "chiliz_spicy",
  "base_goerli",
  "optimism_goerli",
  "klaytn_baobab",
  "celo_alfajores",
  "kava_testnet",
  "metis_goerli",
  "harmony_testnet",
  "zkSync_testnet",
  "aurora_testnet",
  "moonbase",
  "fuse_testnet",
  "linea_testnet",
  "scroll_testnet",
  "holesky",
  "polygon_amoy",
  "base_sepolia",
  "optimism_sepolia",
  "arbitrum_sepolia",
  "linea_sepolia",
  "blast_sepolia",
  "mantle_sepolia",
  "zksync_sepolia",
  "polygon_zkevm_cardona",
  "fantom_testnet",
  "lisk_sepolia",
  "flow_testnet",
  "ronin_saigon",
  "zetachain_testnet",
  "sei_testnet",
  "hyperevm_testnet"
];
var LAYER1_CHAINS = [
  "eth",
  "etc",
  "bsc",
  "avalanche",
  "fantom",
  "klaytn",
  "celo",
  "kava",
  "harmony",
  "moonriver",
  "moonbeam",
  "fuse",
  "pulsechain",
  "flow",
  "ronin",
  "sei",
  "monad",
  "peaq",
  "zetachain",
  "hyperevm"
];
var LAYER2_CHAINS = [
  "polygon",
  "arbitrum",
  "optimism",
  "metis",
  "base",
  "zkSync_era",
  "linea",
  "scroll",
  "mantle",
  "opbnb",
  "polygon_zkevm",
  "blast",
  "lisk",
  "grvt",
  "ink",
  "robinhood"
];
var SIDECHAINS = ["palm", "gnosis", "cronos", "aurora"];
var CHAIN_CONFIG = {
  "0x1": { token: "ETH", gasUnit: "Gwei", name: "Ethereum" },
  "0x3d": { token: "ETC", gasUnit: "Gwei", name: "Ethereum Classic" },
  "0x89": { token: "MATIC", gasUnit: "Gwei", name: "Polygon" },
  "0x38": { token: "BNB", gasUnit: "Gwei", name: "BNB Chain" },
  // Gas on Arbitrum is ETH; ARB is a governance token.
  "0xa4b1": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum" },
  "0x2105": { token: "ETH", gasUnit: "Gwei", name: "Base" },
  "0xa": { token: "ETH", gasUnit: "Gwei", name: "Optimism" },
  "0xe708": { token: "ETH", gasUnit: "Gwei", name: "Linea" },
  "0xa86a": { token: "AVAX", gasUnit: "nAVAX", name: "Avalanche" },
  "0xfa": { token: "FTM", gasUnit: "Gwei", name: "Fantom" },
  "0x19": { token: "CRO", gasUnit: "Gwei", name: "Cronos" },
  "0x64": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis" },
  "0x15b38": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz" },
  "0x171": { token: "PLS", gasUnit: "Gwei", name: "PulseChain" },
  "0x504": { token: "GLMR", gasUnit: "Gwei", name: "Moonbeam" },
  "0x505": { token: "MOVR", gasUnit: "Gwei", name: "Moonriver" },
  "0x13e31": { token: "ETH", gasUnit: "Gwei", name: "Blast" },
  "0x144": { token: "ETH", gasUnit: "Gwei", name: "zkSync Era" },
  "0x1388": { token: "MNT", gasUnit: "Gwei", name: "Mantle" },
  "0xa1e3": { token: "NES", gasUnit: "Gwei", name: "Nesa" },
  "0xd0a": { token: "PEAQ", gasUnit: "Gwei", name: "peaq" },
  // GRVT pays gas in GBT (its ZKsync Bridgehub base token), not ETH.
  "0x145": { token: "GBT", gasUnit: "Gwei", name: "GRVT" },
  "0xcc": { token: "BNB", gasUnit: "Gwei", name: "opBNB" },
  // Gas on Polygon zkEVM is ETH, not MATIC.
  "0x44d": { token: "ETH", gasUnit: "Gwei", name: "Polygon zkEVM" },
  "0x1b58": { token: "ZETA", gasUnit: "Gwei", name: "ZetaChain" },
  "0x82750": { token: "ETH", gasUnit: "Gwei", name: "Scroll" },
  "0x4e454152": { token: "ETH", gasUnit: "Gwei", name: "Aurora" },
  "0x531": { token: "SEI", gasUnit: "Gwei", name: "Sei" },
  "0x3e7": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM" },
  "0xdef1": { token: "ETH", gasUnit: "Gwei", name: "Ink" },
  "0x1237": { token: "ETH", gasUnit: "Gwei", name: "Robinhood Chain" },
  "0x3e6": { token: "HYPE", gasUnit: "Gwei", name: "HyperEVM Testnet" },
  "0x8f": { token: "MON", gasUnit: "Gwei", name: "Monad" },
  "0x3e9": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn Baobab" },
  "0x4e454153": { token: "ETH", gasUnit: "Gwei", name: "Aurora Testnet" },
  "0x8274f": { token: "ETH", gasUnit: "Gwei", name: "Scroll Sepolia" },
  "0x530": { token: "SEI", gasUnit: "Gwei", name: "Sei Testnet" },
  "0x2eb": { token: "FLOW", gasUnit: "FLOW units", name: "Flow" },
  "0x7e4": { token: "RON", gasUnit: "Gwei", name: "Ronin" },
  "0x46f": { token: "LSK", gasUnit: "LSK units", name: "Lisk" },
  "0xaa36a7": { token: "ETH", gasUnit: "Gwei", name: "Sepolia" },
  "0x4268": { token: "ETH", gasUnit: "Gwei", name: "Holesky" },
  "0x13882": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Amoy" },
  "0x61": { token: "tBNB", gasUnit: "Gwei", name: "BSC Testnet" },
  "0x66eee": { token: "ETH", gasUnit: "Gwei", name: "Arbitrum Sepolia" },
  "0x14a34": { token: "ETH", gasUnit: "Gwei", name: "Base Sepolia" },
  "0xaa37dc": { token: "ETH", gasUnit: "Gwei", name: "Optimism Sepolia" },
  "0xe705": { token: "ETH", gasUnit: "Gwei", name: "Linea Sepolia" },
  "0xfa2": { token: "FTM", gasUnit: "Gwei", name: "Fantom Testnet" },
  "0x27d8": { token: "xDAI", gasUnit: "Gwei", name: "Gnosis Chiado" },
  "0x15b32": { token: "CHZ", gasUnit: "Gwei", name: "Chiliz Testnet" },
  "0x507": { token: "DEV", gasUnit: "Gwei", name: "Moonbase" },
  "0xa0c71fd": { token: "ETH", gasUnit: "Gwei", name: "Blast Sepolia" },
  "0x12c": { token: "ETH", gasUnit: "Gwei", name: "zkSync Sepolia" },
  "0x138b": { token: "MNT", gasUnit: "Gwei", name: "Mantle Sepolia" },
  "0x98a": { token: "MATIC", gasUnit: "Gwei", name: "Polygon zkEVM Cardona" },
  "0x1b59": { token: "ZETA", gasUnit: "Gwei", name: "Zetachain Testnet" },
  "0x221": { token: "FLOW", gasUnit: "FLOW units", name: "Flow Testnet" },
  "0x7e5": { token: "RON", gasUnit: "Gwei", name: "Ronin Saigon Testnet" },
  "0x106a": { token: "LSK", gasUnit: "LSK units", name: "Lisk Sepolia" },
  "0x0": { token: "BTC", gasUnit: "Sats/byte", name: "Bitcoin" },
  "0x1399c": { token: "SOL", gasUnit: "Lamports", name: "Solana" },
  "0x162": { token: "DOT", gasUnit: "Plancks", name: "Polkadot" },
  "0x2611": { token: "XPL", gasUnit: "Gwei", name: "Plasma" },
  // Without a row, `seedFromCanonical` names a chain by its slug ("celo") and
  // gives it ETH. Every seeded chain has one; the rows below were added in
  // `_seed_data.py` first and ported, and the parity test keeps them in step.
  "0xa4ec": { token: "CELO", gasUnit: "Gwei", name: "Celo" },
  "0x440": { token: "METIS", gasUnit: "Gwei", name: "Metis" },
  "0x8ae": { token: "KAVA", gasUnit: "Gwei", name: "Kava" },
  "0x63564c40": { token: "ONE", gasUnit: "Gwei", name: "Harmony" },
  "0x7a": { token: "FUSE", gasUnit: "Gwei", name: "Fuse" },
  "0x2019": { token: "KLAY", gasUnit: "Gwei", name: "Klaytn" },
  "0x2a15c308d": { token: "PALM", gasUnit: "Gwei", name: "Palm" },
  "0x5": { token: "ETH", gasUnit: "Gwei", name: "Goerli" },
  "0x13881": { token: "MATIC", gasUnit: "Gwei", name: "Polygon Mumbai" },
  "0x14a33": { token: "ETH", gasUnit: "Gwei", name: "Base Goerli" },
  "0x1a4": { token: "ETH", gasUnit: "Gwei", name: "Optimism Goerli" },
  "0xaef3": { token: "CELO", gasUnit: "Gwei", name: "Celo Alfajores" },
  "0x8ad": { token: "TKAVA", gasUnit: "Gwei", name: "Kava Testnet" },
  "0x24c": { token: "METIS", gasUnit: "Gwei", name: "Metis Goerli" },
  "0x6357d2e0": { token: "ONE", gasUnit: "Gwei", name: "Harmony Testnet" },
  "0x118": { token: "ETH", gasUnit: "Gwei", name: "zkSync Goerli" },
  "0x7b": { token: "SPARK", gasUnit: "Gwei", name: "Fuse Sparknet" },
  "0xe704": { token: "ETH", gasUnit: "Gwei", name: "Linea Goerli" }
};
var EXPLORER_URLS = {
  eth: "https://etherscan.io",
  etc: "https://blockscout.com/etc/mainnet",
  polygon: "https://polygonscan.com",
  bsc: "https://bscscan.com",
  avalanche: "https://snowtrace.io",
  fantom: "https://ftmscan.com",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  zksync_era: "https://explorer.zksync.io",
  base: "https://basescan.org",
  linea: "https://lineascan.build",
  scroll: "https://scrollscan.com",
  mantle: "https://explorer.mantle.xyz",
  // The EVM-side Blockscout. Nesa also runs a Cosmos explorer at
  // `explorer.nesa.ai`, which cannot resolve an `0x` address or tx hash.
  nesa: "https://explorer-evm.nesa.ai",
  // Subscan, whose EVM mode answers the default /tx, /address, /block and
  // /token paths (each checked against a live peaq tx on 2026-09-16).
  peaq: "https://peaq.subscan.io",
  opbnb: "https://opbnbscan.com",
  polygon_zkevm: "https://zkevm.polygonscan.com",
  blast: "https://blastscan.io",
  pulsechain: "https://scan.pulsechain.com",
  aurora: "https://explorer.aurora.dev",
  flow: "https://evm.flowscan.io",
  ronin: "https://app.roninchain.com",
  lisk: "https://blockscout.lisk.com",
  zetachain: "https://explorer.zetachain.com",
  sei: "https://seitrace.com",
  hyperevm: "https://hyperevmscan.io",
  ink: "https://explorer.inkonchain.com",
  robinhood: "https://robinhoodchain.blockscout.com",
  monad: "https://monadexplorer.com",
  plasma: "https://plasmascan.to",
  // --- Testnets ---
  // A testnet with no row here gets NO explorer link — the registry does not
  // lend it its mainnet's, which would show a different chain's object under
  // the same hash or address. So a row belongs here only when the testnet has
  // its own explorer that is live; these were checked on 2026-09-16 (DNS, the
  // site answering, and the chain id where the explorer exposes one).
  //
  // Deliberately absent, because the explorer is gone or now serves another
  // network: holesky (holesky.etherscan.io no longer resolves), mumbai
  // (mumbai.polygonscan.com no longer resolves — the Python table still carries
  // it), base_goerli, optimism_goerli, linea_testnet, zkSync_testnet,
  // metis_goerli, polygon_zkevm_cardona, fantom_testnet, celo_alfajores (both
  // its explorers redirect to Celo Sepolia), scroll_testnet
  // (sepolia.scrollscan.com is a Cloudflare 1014), lisk_sepolia (unreachable)
  // and sei_testnet (Seitrace picks the network by query string, which the link
  // builder cannot express).
  //
  // FE-only: the Python table has no mainnet fallback to correct, and feeds
  // scraping hosts, so these are not mirrored there.
  goerli: "https://goerli.etherscan.io",
  sepolia: "https://sepolia.etherscan.io",
  polygon_amoy: "https://amoy.polygonscan.com",
  bsc_testnet: "https://testnet.bscscan.com",
  gnosis_chiado: "https://gnosis-chiado.blockscout.com",
  base_sepolia: "https://sepolia.basescan.org",
  optimism_sepolia: "https://sepolia-optimism.etherscan.io",
  arbitrum_sepolia: "https://sepolia.arbiscan.io",
  aurora_testnet: "https://explorer.testnet.aurora.dev",
  moonbase: "https://moonbase.moonscan.io",
  fuse_testnet: "https://explorer.fusespark.io",
  linea_sepolia: "https://sepolia.lineascan.build",
  blast_sepolia: "https://sepolia.blastscan.io",
  mantle_sepolia: "https://sepolia.mantlescan.xyz",
  zksync_sepolia: "https://sepolia.explorer.zksync.io",
  flow_testnet: "https://testnet.evm.flow.com",
  ronin_saigon: "https://saigon-app.roninchain.com",
  zetachain_testnet: "https://testnet.zetascan.com"
};
var TESTNET_TO_MAINNET = {
  goerli: "eth",
  sepolia: "eth",
  mumbai: "polygon",
  bsc_testnet: "bsc",
  gnosis_chiado: "gnosis",
  chiliz_spicy: "chiliz",
  base_goerli: "base",
  optimism_goerli: "optimism",
  klaytn_baobab: "klaytn",
  celo_alfajores: "celo",
  kava_testnet: "kava",
  metis_goerli: "metis",
  harmony_testnet: "harmony",
  zkSync_testnet: "zkSync_era",
  aurora_testnet: "aurora",
  moonbase: "moonbeam",
  fuse_testnet: "fuse",
  linea_testnet: "linea",
  scroll_testnet: "scroll",
  holesky: "eth",
  polygon_amoy: "polygon",
  base_sepolia: "base",
  optimism_sepolia: "optimism",
  arbitrum_sepolia: "arbitrum",
  linea_sepolia: "linea",
  blast_sepolia: "blast",
  mantle_sepolia: "mantle",
  zksync_sepolia: "zkSync_era",
  polygon_zkevm_cardona: "polygon_zkevm",
  fantom_testnet: "fantom",
  lisk_sepolia: "lisk",
  flow_testnet: "flow",
  ronin_saigon: "ronin",
  zetachain_testnet: "zetachain",
  sei_testnet: "sei",
  hyperevm_testnet: "hyperevm"
};
var CHAIN_ALIASES = {
  "eth": ["ethereum", "ethereum-mainnet", "eth-mainnet"],
  "polygon": ["matic", "matic-network", "polygon-mainnet", "polygon-pos"],
  "bsc": ["binance", "binance-smart-chain", "bnb", "bnb-chain", "bnb-smart-chain"],
  "avalanche": ["avax", "avalanche-c-chain", "avax-c-chain", "avalanche-mainnet"],
  "arbitrum": ["arbitrum-one", "arb", "arbitrum-mainnet"],
  "optimism": ["op", "op-mainnet", "optimism-mainnet"],
  "base": ["base-mainnet"],
  "gnosis": ["xdai", "gnosis-chain"],
  "fantom": ["ftm", "fantom-opera"],
  "cronos": ["cro"],
  "linea": ["linea-mainnet"],
  "scroll": ["scroll-mainnet"],
  "blast": ["blast-mainnet"],
  "ronin": ["ron"],
  "sei": ["sei-mainnet"],
  "pulsechain": ["pls"],
  "polygon_zkevm": ["polygon-zkevm", "zkevm"],
  "zksync_era": ["zksync", "zksync-era", "era"],
  "mantle_sepolia": ["mantle-testnet", "mantle_testnet"],
  // Non-EVM — the short tickers callers reach for.
  "solana": ["sol"],
  "bitcoin": ["btc"],
  "polkadot": ["dot"],
  "hypercore": ["hyperliquid", "hyperliquid-core", "hyperliquid-l1"],
  "hyperevm": ["hyper-evm", "hyperliquid-evm"],
  "hypercore-testnet": ["hyperliquid-testnet", "hypercore_testnet"],
  "hyperevm_testnet": ["hyperevm-testnet", "hyperliquid-evm-testnet"],
  "monero": ["xmr"],
  "zcash": ["zec"]
};

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key2, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key2);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}

// src/chains/registry.ts
function foldAliases(...groups) {
  const seen = /* @__PURE__ */ new Set();
  for (const group of groups) {
    for (const raw of group ?? []) {
      const alias = raw.trim().toLowerCase();
      if (alias) seen.add(alias);
    }
  }
  return [...seen];
}
var ChainRegistry = class {
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  byCaip2 = /* @__PURE__ */ new Map();
  byAlias = /* @__PURE__ */ new Map();
  register(chain, { replace = false } = {}) {
    const existingSlug = this.bySlug.get(chain.slug);
    if (existingSlug && existingSlug !== chain && !replace) {
      throw new Error(
        `Chain slug "${chain.slug}" already registered; pass replace=true to overwrite.`
      );
    }
    if (chain.chainIdHex) {
      const existingHex = this.byHex.get(chain.chainIdHex);
      if (existingHex && existingHex.slug !== chain.slug && !replace) {
        throw new Error(
          `Chain hex ${chain.chainIdHex} already mapped to slug ${existingHex.slug}; pass replace=true to overwrite.`
        );
      }
    }
    if (existingSlug) this.dropAliases(existingSlug);
    this.bySlug.set(chain.slug, chain);
    if (chain.chainIdHex) this.byHex.set(chain.chainIdHex, chain);
    if (chain.caip2) this.byCaip2.set(chain.caip2, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
  }
  dropAliases(chain) {
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  unregister(slug) {
    const chain = this.bySlug.get(slug);
    if (!chain) return null;
    this.bySlug.delete(slug);
    if (chain.chainIdHex) this.byHex.delete(chain.chainIdHex);
    if (chain.caip2) this.byCaip2.delete(chain.caip2);
    this.dropAliases(chain);
    return chain;
  }
  clear() {
    this.bySlug.clear();
    this.byHex.clear();
    this.byCaip2.clear();
    this.byAlias.clear();
  }
  get(slug) {
    return this.bySlug.get(slug) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes + lowercases). */
  getByHex(chainIdHex) {
    let normalized2 = chainIdHex.toLowerCase();
    if (!normalized2.startsWith("0x")) normalized2 = "0x" + normalized2.replace(/^0x/, "");
    return this.byHex.get(normalized2) ?? null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"). */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2)) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire.
   */
  resolve(value) {
    if (!value) return null;
    if (value.includes(":")) {
      const hit = this.getByCaip2(value);
      if (hit) return hit;
    }
    if (value.startsWith("0x")) {
      const hit = this.getByHex(value);
      if (hit) return hit;
    }
    if (/^\d+$/.test(value)) {
      const hit = this.getByHex("0x" + BigInt(value).toString(16));
      if (hit) return hit;
    }
    const folded = value.toLowerCase();
    return this.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  byFamily(family) {
    return [...this.bySlug.values()].filter((c) => c.family === family).sort((a, b) => a.slug.localeCompare(b.slug));
  }
  all() {
    return [...this.bySlug.values()].sort((a, b) => a.slug.localeCompare(b.slug));
  }
};
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function toCanonicalChainId(value) {
  if (!value) return null;
  const chain = registry.resolve(value);
  if (!chain) return (value.includes(":") ? normalizeCaip2(value) : value.trim().toLowerCase()) || null;
  return chain.caip2 ?? chain.chainIdHex ?? chain.slug;
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var REORG_DEPTHS_BY_SLUG = {
  eth: 12,
  polygon: 256,
  matic: 256,
  bsc: 15,
  arbitrum: 12,
  optimism: 12,
  base: 12,
  avalanche: 12,
  fantom: 12,
  cronos: 12,
  gnosis: 12,
  linea: 12,
  scroll: 12,
  mantle: 12,
  zksync: 12,
  zksync_era: 12,
  metis: 12,
  celo: 12,
  klaytn: 12,
  moonbeam: 12,
  moonriver: 12,
  harmony: 12,
  aurora: 12,
  fuse: 12,
  palm: 12,
  chiliz: 12,
  kava: 12,
  blast: 12,
  bitcoin: 6,
  solana: 32,
  // Monero's ten-block spend lock is a network rule; Zcash needs many more
  // blocks than Bitcoin for equal work at a 75-second block.
  monero: 10,
  zcash: 24,
  // HyperBFT commits a block final.
  hypercore: 1,
  hyperevm: 1,
  hyperevm_testnet: 1
};
var BLOCK_TIME_BY_SLUG = {
  eth: 12,
  etc: 13,
  polygon: 2,
  matic: 2,
  bsc: 3,
  arbitrum: 0.25,
  optimism: 2,
  base: 2,
  avalanche: 2,
  fantom: 1,
  cronos: 6,
  gnosis: 5,
  linea: 3,
  scroll: 3,
  mantle: 2,
  zksync: 1,
  zksync_era: 1,
  metis: 4,
  celo: 5,
  klaytn: 1,
  moonbeam: 12,
  moonriver: 12,
  harmony: 2,
  aurora: 1,
  fuse: 5,
  blast: 2,
  bitcoin: 600,
  solana: 0.4,
  polkadot: 6,
  monero: 120,
  zcash: 75,
  hypercore: 0.2,
  "hypercore-testnet": 0.2,
  hyperevm: 1
};
var EXPLORER_SUPPLEMENT = {
  thorchain: "https://thorchain.net",
  near: "https://nearblocks.io",
  "near-testnet": "https://testnet.nearblocks.io",
  xrp: "https://xrpscan.com",
  "xrp-testnet": "https://testnet.xrpscan.com",
  tron: "https://tronscan.org/#",
  "tron-shasta": "https://shasta.tronscan.org/#",
  "tron-nile": "https://nile.tronscan.org/#",
  bitcoin: "https://blockstream.info",
  solana: "https://solscan.io",
  polkadot: "https://polkadot.subscan.io",
  monero: "https://xmrchain.net",
  // Blockchair's base carries the coin as a path segment, which is exactly
  // what the link builder appends to — `/zcash` + `/transaction/<hash>`.
  zcash: "https://blockchair.com/zcash",
  // app.hyperliquid.xyz/explorer/tx/<hash>, /address/<addr>, /block/<n>.
  hypercore: "https://app.hyperliquid.xyz/explorer",
  "hypercore-testnet": "https://app.hyperliquid-testnet.xyz/explorer",
  cronos: "https://cronoscan.com",
  gnosis: "https://gnosisscan.io",
  celo: "https://celoscan.io",
  moonbeam: "https://moonscan.io",
  zkSync_era: "https://explorer.zksync.io",
  matic: "https://polygonscan.com"
};
function explorerFor(slug, rawSlug) {
  return EXPLORER_URLS[slug] ?? EXPLORER_URLS[rawSlug] ?? EXPLORER_SUPPLEMENT[slug] ?? EXPLORER_SUPPLEMENT[rawSlug] ?? null;
}
var BRAND_COLOR_BY_SLUG = {
  eth: "#627EEA",
  etc: "#328332",
  polygon: "#8247E5",
  matic: "#8247E5",
  bsc: "#F0B90B",
  arbitrum: "#28A0F0",
  optimism: "#FF0420",
  base: "#0052FF",
  avalanche: "#E84142",
  fantom: "#1969FF",
  cronos: "#002D74",
  gnosis: "#0B6E4F",
  linea: "#61DFFF",
  scroll: "#EBC28E",
  mantle: "#000000",
  zksync: "#8C8DFC",
  zksync_era: "#8C8DFC",
  metis: "#00DACC",
  celo: "#FCFF52",
  klaytn: "#FF1B1B",
  moonbeam: "#53CBC8",
  moonriver: "#C0C0C0",
  harmony: "#00AEE9",
  aurora: "#70D44B",
  fuse: "#B4F9BA",
  blast: "#FCFC03",
  kava: "#FF433E",
  bitcoin: "#F7931A",
  solana: "#14F195",
  polkadot: "#E6007A",
  monero: "#FF6600",
  zcash: "#F4B728",
  hypercore: "#97FCE4",
  hyperevm: "#97FCE4"
};
function brandColorFor(slug, mainnetSlug) {
  return BRAND_COLOR_BY_SLUG[slug] ?? (mainnetSlug ? BRAND_COLOR_BY_SLUG[mainnetSlug] ?? null : null);
}
var LOGO_URL_BY_SLUG = {};
var CAIP2_BY_SLUG = {
  thorchain: "cosmos:thorchain-1",
  near: "near:mainnet",
  "near-testnet": "near:testnet",
  xrp: "xrpl:0",
  "xrp-testnet": "xrpl:1",
  tron: "tron:728126428",
  "tron-shasta": "tron:2494104990",
  "tron-nile": "tron:3448148188",
  "bitcoin-testnet": "bip122:000000000933ea01ad0ee984209779ba",
  "solana-devnet": "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
  "solana-testnet": "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
  bitcoin: "bip122:000000000019d6689c085ae165831e93",
  solana: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
  polkadot: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
  // Zcash is a Bitcoin fork, so `bip122` is its registered namespace and the
  // reference is its genesis hash truncated to 32 chars — Bitcoin's own rule.
  zcash: "bip122:00040fe8ec8471911baa1db1266ea15d",
  // Monero has no CASA-registered namespace; `monero` is a platform-local
  // choice. See the note on the Python side, which this table must match
  // byte for byte — `test_chains_fe_registry_parity.py` fails if it drifts.
  monero: "monero:418015bb9ae982a1975da7d79277c270",
  // HyperCore has no CASA namespace, and `hyperliquid` / `hypercore` exceed
  // CAIP-2's eight-character namespace limit. Platform-local; matches Python.
  hypercore: "hyperliq:mainnet",
  "hypercore-testnet": "hyperliq:testnet"
};
function deriveCaip2(slug, family, chainIdHex) {
  if (CAIP2_BY_SLUG[slug]) return CAIP2_BY_SLUG[slug];
  if (family === ChainFamily.EVM && chainIdHex) {
    return `eip155:${BigInt(chainIdHex).toString(10)}`;
  }
  return null;
}
var NON_EVM_OVERRIDES = {
  // Account-ledger networks mirrored from commons.
  xrp: { family: ChainFamily.XRPL, decimals: 6, chainIdHex: null, name: "XRP Ledger", nativeToken: "XRP", aliases: ["xrpl", "ripple", "xrp-mainnet", "xrpl-mainnet"] },
  "xrp-testnet": { family: ChainFamily.XRPL, decimals: 6, chainIdHex: null, name: "XRP Ledger Testnet", nativeToken: "XRP", aliases: ["xrpl-testnet"], mainnetSlug: "xrp" },
  tron: { family: ChainFamily.TRON, decimals: 6, chainIdHex: null, name: "TRON", nativeToken: "TRX", aliases: ["trx", "tron-mainnet", "tron:0x2b6653dc"] },
  "tron-shasta": { family: ChainFamily.TRON, decimals: 6, chainIdHex: null, name: "TRON Shasta", nativeToken: "TRX", aliases: ["tron-testnet-shasta", "tron:0x94a9059e"], mainnetSlug: "tron" },
  "tron-nile": { family: ChainFamily.TRON, decimals: 6, chainIdHex: null, name: "TRON Nile", nativeToken: "TRX", aliases: ["tron-testnet-nile", "tron:0xcd8690dc"], mainnetSlug: "tron" },
  "bitcoin-testnet": { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin Testnet", nativeToken: "BTC", mainnetSlug: "bitcoin" },
  "solana-devnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Devnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "solana-testnet": { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana Testnet", nativeToken: "SOL", mainnetSlug: "solana" },
  "hypercore-testnet": { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore Testnet", nativeToken: "HYPE", mainnetSlug: "hypercore" },
  bitcoin: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Bitcoin", nativeToken: "BTC", aliases: ["bitcoin-mainnet", "0x0"] },
  solana: { family: ChainFamily.SOLANA, decimals: 9, chainIdHex: null, name: "Solana", nativeToken: "SOL", aliases: ["solana-mainnet", "0x1399c"] },
  thorchain: { family: ChainFamily.COSMOS, decimals: 8, chainIdHex: null, name: "THORChain", nativeToken: "RUNE", aliases: ["thorchain-mainnet"] },
  near: { family: ChainFamily.NEAR, decimals: 24, chainIdHex: null, name: "NEAR", nativeToken: "NEAR", aliases: ["near-mainnet"] },
  "near-testnet": { family: ChainFamily.NEAR, decimals: 24, chainIdHex: null, name: "NEAR Testnet", nativeToken: "NEAR", mainnetSlug: "near" },
  polkadot: { family: ChainFamily.OTHER, decimals: 10, chainIdHex: null, name: "Polkadot", nativeToken: "DOT", aliases: ["polkadot-mainnet", "0x162"] },
  monero: { family: ChainFamily.UTXO, decimals: 12, chainIdHex: null, name: "Monero", nativeToken: "XMR", aliases: ["monero-mainnet"] },
  zcash: { family: ChainFamily.UTXO, decimals: 8, chainIdHex: null, name: "Zcash", nativeToken: "ZEC", aliases: ["zcash-mainnet"] },
  hypercore: { family: ChainFamily.HYPERCORE, decimals: 8, chainIdHex: null, name: "HyperCore", nativeToken: "HYPE", aliases: ["hypercore-mainnet"] }
};
function layerFor(slug) {
  const folded = slug.toLowerCase();
  const listedIn = (listed) => listed.some((member) => member.toLowerCase() === folded);
  if (listedIn(LAYER1_CHAINS)) return "l1";
  if (listedIn(LAYER2_CHAINS)) return "l2";
  if (listedIn(SIDECHAINS)) return "sidechain";
  return "unknown";
}
function seedFromCanonical(target) {
  for (const [hexId, rawSlug] of Object.entries(CHAIN_MAPPING)) {
    const slug = rawSlug.toLowerCase();
    const config = CHAIN_CONFIG[hexId];
    const token = config?.token ?? "ETH";
    const override = NON_EVM_OVERRIDES[slug];
    const family = override?.family ?? ChainFamily.EVM;
    const decimals = override?.decimals ?? 18;
    const isTestnet = TESTNET_CHAINS.includes(rawSlug);
    let mainnetSlug = null;
    if (isTestnet) {
      const m = TESTNET_TO_MAINNET[rawSlug];
      if (m) mainnetSlug = m.toLowerCase();
    }
    const reorgDepth = REORG_DEPTHS_BY_SLUG[slug] ?? 12;
    const chainIdHex = hexId.toLowerCase();
    const metadata = { layer: layerFor(slug) };
    if (config?.gasUnit) metadata.gasUnit = config.gasUnit;
    if (slug === "etc") {
      metadata.fork_of = "eth";
      metadata.note = "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk.";
    }
    try {
      target.register(
        {
          slug,
          chainIdHex,
          family,
          name: config?.name ?? rawSlug,
          nativeToken: token,
          decimals,
          isTestnet,
          mainnetSlug,
          reorgDepth,
          explorerUrl: explorerFor(slug, rawSlug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? (mainnetSlug ? BLOCK_TIME_BY_SLUG[mainnetSlug] ?? null : null),
          caip2: deriveCaip2(slug, family, chainIdHex),
          brandColor: brandColorFor(slug, mainnetSlug),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? (mainnetSlug ? LOGO_URL_BY_SLUG[mainnetSlug] ?? null : null),
          metadata,
          aliases: foldAliases(override?.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
  for (const [slug, ovr] of Object.entries(NON_EVM_OVERRIDES)) {
    if (target.get(slug)) continue;
    try {
      target.register(
        {
          slug,
          chainIdHex: ovr.chainIdHex,
          family: ovr.family,
          name: ovr.name,
          nativeToken: ovr.nativeToken,
          decimals: ovr.decimals,
          isTestnet: !!ovr.mainnetSlug,
          mainnetSlug: ovr.mainnetSlug ?? null,
          reorgDepth: REORG_DEPTHS_BY_SLUG[slug] ?? REORG_DEPTHS_BY_SLUG[ovr.mainnetSlug ?? ""] ?? 12,
          explorerUrl: explorerFor(slug, slug),
          blockTimeSec: BLOCK_TIME_BY_SLUG[slug] ?? null,
          caip2: deriveCaip2(slug, ovr.family, ovr.chainIdHex),
          brandColor: brandColorFor(slug, null),
          logoUrl: LOGO_URL_BY_SLUG[slug] ?? null,
          metadata: {},
          aliases: foldAliases(ovr.aliases, CHAIN_ALIASES[slug])
        },
        { replace: true }
      );
    } catch {
      continue;
    }
  }
}
var registry = globalSingleton("chains/registry", () => {
  const seeded = new ChainRegistry();
  seedFromCanonical(seeded);
  return seeded;
});

// src/display/truncation.ts
var ELLIPSIS = "\u2026";
var DEFAULT_TRUNCATION = {
  hash: { head: 10, tail: 8 },
  address: { head: 6, tail: 4 }
};
var truncation = globalSingleton("display/truncation", () => ({
  active: DEFAULT_TRUNCATION
}));
function getTruncation(kind) {
  const { active } = truncation;
  return kind === "hash" ? active.hash : active.address;
}
function hexPrefixOf(value) {
  return /^0x/i.test(value) ? value.slice(0, 2) : "";
}
function truncateMiddle(value, lengths) {
  const text = value.trim();
  if (/\s/.test(text)) return value;
  const prefix = hexPrefixOf(text);
  const body = text.slice(prefix.length);
  if (body.length <= lengths.head + lengths.tail + ELLIPSIS.length) return text;
  return `${prefix}${body.slice(0, lengths.head)}${ELLIPSIS}${body.slice(-lengths.tail)}`;
}

// src/display/coerce.ts
function asString(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}
function asTrimmedString(value) {
  const text = typeof value === "string" ? value.trim() : "";
  return text === "" ? void 0 : text;
}
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
var BITCOIN_ADDRESS_NETWORKS = {
  bitcoin: { hrp: "bc", versions: [0, 5] },
  "bitcoin-testnet": { hrp: "tb", versions: [111, 196] },
  "bitcoin-regtest": { hrp: "bcrt", versions: [111, 196] }
};
function decodeBase58(address, alphabet = BASE58) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== alphabet[0]) break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower2 = address.toLowerCase();
  if (address !== lower2 && address !== address.toUpperCase()) return null;
  const separator = lower2.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower2.slice(0, separator);
  const data = [...lower2.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function validBitcoinAddress(address, network) {
  if (address.length < 14 || address.length > 90) return false;
  const formats = Object.values(BITCOIN_ADDRESS_NETWORKS);
  if (!formats.length) return false;
  if (/^(bc1|tb1|bcrt1)/i.test(address)) {
    const decoded = decodeBech32(address);
    if (!decoded || !formats.some((format) => format.hrp === decoded.hrp) || !decoded.data.length) return false;
    const version = decoded.data[0];
    if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
    const length = convertedLength(decoded.data.slice(1));
    return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && formats.some((format) => format.versions.includes(payload[0]));
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}
function validLedgerAddress(address, family) {
  const alphabet = family === "xrpl" ? "rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz" : BASE58;
  const bytes = decodeBase58(address, alphabet);
  if (!bytes || bytes.length !== 25 || bytes[0] !== (family === "xrpl" ? 0 : 65)) return false;
  const digest = sha256(sha256(bytes.slice(0, -4)));
  return bytes.slice(-4).every((value, index) => value === digest[index]);
}

// src/chains/tx.ts
var RE_HEX64 = /^(0x)?[0-9a-fA-F]{64}$/i;
var RE_SOLANA_SIG = /^[1-9A-HJ-NP-Za-km-z]{64,88}$/;
function isValidTxHash(txHash, chainId) {
  const h = txHash.trim();
  if (!h) return false;
  const fam = null;
  if (hasEvmIdentifiers(fam) || fam === ChainFamily.UTXO) return RE_HEX64.test(h);
  return RE_HEX64.test(h) || RE_SOLANA_SIG.test(h);
}

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var RE_XRPL = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
var RE_TRON = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validBitcoinAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_XRPL.test(address) && validLedgerAddress(address, "xrpl")) return ChainFamily.XRPL;
  if (RE_TRON.test(address) && validLedgerAddress(address, "tron")) return ChainFamily.TRON;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function normalizeAddress(address, chainId) {
  const addr = address.trim();
  const fam = addressFamily(addr);
  if (hasEvmIdentifiers(fam)) {
    const hex = addr.toLowerCase();
    return hex.startsWith("0x") ? hex : `0x${hex}`;
  }
  if (fam === ChainFamily.COSMOS && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  if (fam === null && /^0x/i.test(addr)) return addr.toLowerCase();
  if (fam === ChainFamily.UTXO && RE_BTC_BECH32.test(addr) && (addr === addr.toLowerCase() || addr === addr.toUpperCase())) return addr.toLowerCase();
  return addr;
}
function shortenAddress(address, lengths) {
  return truncateMiddle(address, { ...getTruncation("address"), ...lengths });
}
function shortenIdentifier(value) {
  return truncateMiddle(value, getTruncation(truncationKindOf(value)));
}
var LONGER_THAN_AN_ADDRESS = /^0x[0-9a-f]{41,}$/i;
function truncationKindOf(value) {
  const text = value.trim();
  return isValidTxHash(text) || LONGER_THAN_AN_ADDRESS.test(text) ? "hash" : "address";
}

// src/funds/sides.ts
function addCounterpartyChain(tally, chain) {
  tally.chains ??= /* @__PURE__ */ new Set();
  tally.chains.add(chain ? toCanonicalChainId(chain) ?? chain : null);
}
function addAsset(into, asset, amount, usd) {
  const seen = into.get(asset.key);
  if (!seen) {
    into.set(asset.key, { asset, amount, usd });
    return;
  }
  seen.amount += amount;
  seen.usd = seen.usd === null || usd === null ? null : seen.usd + usd;
}
function count(value) {
  return typeof value === "number" ? value : value.size;
}
function assetList(tallies) {
  if (!tallies || tallies.size === 0) return void 0;
  return [...tallies.values()].map((t) => ({ asset: t.asset, amount: t.amount, usd: t.usd })).sort((a, b) => (b.usd ?? -1) - (a.usd ?? -1) || b.amount - a.amount);
}
function buildSide(tallies, unpriced, transactions, pinned = []) {
  const rank = new Map(pinned.map((category, index) => [category, index]));
  const pinRank = (category) => rank.get(category) ?? Number.MAX_SAFE_INTEGER;
  let usdTotal = 0;
  for (const tally of tallies.values()) usdTotal += tally.usd;
  const buckets = [...tallies.entries()].map(([category, tally]) => {
    const top = [...tally.counterparties.values()].map((c) => ({
      address: c.address,
      ...c.chains?.size === 1 && !c.chains.has(null) ? { chainId: [...c.chains][0] } : {},
      usd: c.usd,
      transactions: count(c.transactions),
      ...c.transactions instanceof Set ? { transactionIds: [...c.transactions] } : {},
      ...c.assets ? { assets: assetList(c.assets) } : {},
      ...c.remoteChains && c.remoteChains.size > 0 ? { remoteChains: [...c.remoteChains].sort() } : {},
      ...c.protocol !== void 0 ? { protocol: c.protocol } : {}
    })).sort((a, b) => b.usd - a.usd || b.transactions - a.transactions).slice(0, MAX_BUCKET_COUNTERPARTIES);
    const bucket = {
      category,
      usd: tally.usd,
      counterparties: tally.counterparties.size,
      transactions: count(tally.transactions),
      ...tally.transactions instanceof Set ? { transactionIds: [...tally.transactions] } : {},
      // Zero denominator means every priced amount was worth nothing, which
      // is a real state (a feed of contract calls). Shares are then 0, not NaN.
      share: usdTotal > 0 ? tally.usd / usdTotal : 0,
      top
    };
    const assets = assetList(tally.assets);
    if (assets) bucket.assets = assets;
    return bucket;
  }).sort(
    (a, b) => pinRank(a.category) - pinRank(b.category) || b.usd - a.usd || b.transactions - a.transactions
  );
  return { buckets, usdTotal, unpricedCount: unpriced, transactions };
}

// src/funds/txFlow.ts
function normalized(value) {
  return asTrimmedString(value)?.toLowerCase() ?? null;
}
function sideAddress(row, side) {
  const address = asTrimmedString(row[side]) ?? asTrimmedString(row[`${side}_address`]);
  return address ? normalizeAddress(address) : null;
}
function emptyFlow() {
  return {
    source: EMPTY_SIDE,
    destination: EMPTY_SIDE,
    internalCount: 0,
    excludedCount: 0,
    droppedUnpricedCount: 0,
    outsideEndsCount: 0,
    tokenValueCount: 0,
    tokenValueUsd: 0,
    tokenValuePendingCount: 0,
    movementPlacedCount: 0,
    passThroughCount: 0
  };
}
function rowValueMovedAsTokens(row) {
  return valueMovedAsTokens(row);
}
function rowPricedUsd(row) {
  const raw = row.value_usd;
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}
function placeMoves(moves, own) {
  const groups = /* @__PURE__ */ new Map();
  for (const move of moves) {
    const from = normalizeAddress(move.from ?? "");
    const to = normalizeAddress(move.to ?? "");
    const fromOurs = own.has(from);
    const toOurs = own.has(to);
    if (fromOurs === toOurs) continue;
    const outbound = fromOurs;
    const counterparty = outbound ? to : from;
    if (!counterparty) continue;
    const key2 = `${outbound ? "out" : "in"}|${move.chainId ?? ""}|${counterparty}`;
    const group = groups.get(key2) ?? { outbound, counterparty, chainId: move.chainId, usd: 0, priced: false };
    if (typeof move.usd === "number" && Number.isFinite(move.usd)) {
      group.usd = (group.usd ?? 0) + move.usd;
      group.priced = true;
    }
    groups.set(key2, group);
  }
  return [...groups.values()].map(({ priced, ...g }) => ({ ...g, usd: priced ? g.usd : null }));
}
function subjectFlowOf(moves, subjectAddresses) {
  const own = new Set([...subjectAddresses].map((a) => normalizeAddress(a)));
  const placements = placeMoves(moves, own);
  if (placements.length === 0) return null;
  const side = (outbound) => {
    const legs = placements.filter((p) => p.outbound === outbound);
    const priced = legs.filter((p) => p.usd !== null);
    return {
      moved: legs.length > 0,
      usd: priced.length > 0 ? priced.reduce((sum, p) => sum + (p.usd ?? 0), 0) : null,
      unpriced: priced.length < legs.length
    };
  };
  const out = side(true);
  const into = side(false);
  return {
    sentUsd: out.usd,
    receivedUsd: into.usd,
    sent: out.moved,
    received: into.moved,
    unpriced: out.unpriced || into.unpriced
  };
}
function valueMovedAsTokens(row) {
  const raw = row.total_value_raw ?? row.value;
  if (raw === void 0 || raw === null || raw === "") return false;
  try {
    return BigInt(String(raw).trim()) === 0n;
  } catch {
    const n = Number(raw);
    return Number.isFinite(n) && n === 0;
  }
}
function fundsFlow(rows, subjectAddresses, options = {}) {
  const opts = typeof options === "function" ? { categoryOf: options } : options;
  const { categoryOf, attributionPending = false, pricedOnly = false } = opts;
  const ruledOut = new Set([...opts.excluded ?? []].map((a) => normalizeAddress(a)).filter(Boolean));
  const own = new Set([...subjectAddresses].map((a) => normalizeAddress(a)).filter(Boolean));
  const bySubjectTransactions = opts.subject === "transactions";
  if (own.size === 0 && !bySubjectTransactions) return emptyFlow();
  const passThrough = /* @__PURE__ */ new Set();
  if (bySubjectTransactions) {
    const senders = /* @__PURE__ */ new Set();
    const recipients = /* @__PURE__ */ new Set();
    for (const row of rows) {
      const from = sideAddress(row, "from");
      const to = sideAddress(row, "to");
      if (from) senders.add(from);
      if (to) recipients.add(to);
    }
    for (const address of senders) if (recipients.has(address)) passThrough.add(address);
  }
  const inflow = /* @__PURE__ */ new Map();
  const outflow = /* @__PURE__ */ new Map();
  const inTxs = /* @__PURE__ */ new Set();
  const outTxs = /* @__PURE__ */ new Set();
  const inUnpricedTxs = /* @__PURE__ */ new Set();
  const outUnpricedTxs = /* @__PURE__ */ new Set();
  let internalCount = 0;
  let excludedCount = 0;
  let droppedUnpricedCount = 0;
  let outsideEndsCount = 0;
  let tokenValueCount = 0;
  let tokenValueUsd = 0;
  let tokenValuePendingCount = 0;
  let movementPlacedCount = 0;
  const record = (outbound, counterparty, usd, chainId, txKey, txId) => {
    const resolved = categoryOf ? categoryOf(counterparty, chainId) : null;
    const category = resolved ?? (attributionPending ? "resolving" : "unattributed");
    const bucketed = outbound ? outflow : inflow;
    (outbound ? outTxs : inTxs).add(txKey);
    if (usd === null) {
      (outbound ? outUnpricedTxs : inUnpricedTxs).add(txKey);
    }
    const tally = bucketed.get(category) ?? { usd: 0, transactions: /* @__PURE__ */ new Set(), counterparties: /* @__PURE__ */ new Map() };
    tally.usd += usd ?? 0;
    tally.transactions.add(txId ?? txKey);
    if (counterparty) {
      const seen = tally.counterparties.get(counterparty) ?? { address: counterparty, usd: 0, transactions: /* @__PURE__ */ new Set() };
      seen.usd += usd ?? 0;
      addCounterpartyChain(seen, chainId);
      seen.transactions.add(txId ?? txKey);
      tally.counterparties.set(counterparty, seen);
    }
    bucketed.set(category, tally);
  };
  const seenRows = /* @__PURE__ */ new Set();
  const knownIds = /* @__PURE__ */ new Set();
  for (const [index, r] of rows.entries()) {
    const txId = opts.transactionId?.(r) ?? asTrimmedString(r.id) ?? asTrimmedString(r.transaction_id) ?? void 0;
    const txKey = txId ?? `unidentified-row:${index}`;
    if (seenRows.has(txKey)) continue;
    seenRows.add(txKey);
    if (txId) knownIds.add(txId);
    const from = sideAddress(r, "from");
    const to = sideAddress(r, "to");
    const usd = rowPricedUsd(r);
    if (bySubjectTransactions) {
      if (pricedOnly && usd === null) {
        droppedUnpricedCount += 1;
        continue;
      }
      if (valueMovedAsTokens(r)) {
        tokenValueCount += 1;
        tokenValueUsd += usd ?? 0;
        continue;
      }
      const chainId = normalized(r.chain_id);
      const fromEnd = from !== null && !passThrough.has(from);
      const toEnd = to !== null && !passThrough.has(to);
      if (!fromEnd && !toEnd) {
        if (from || to) internalCount += 1;
        continue;
      }
      if (fromEnd && !ruledOut.has(from)) record(false, from, usd, chainId, txKey, txId);
      else if (fromEnd) excludedCount += 1;
      if (toEnd && !ruledOut.has(to)) record(true, to, usd, chainId, txKey, txId);
      else if (toEnd) excludedCount += 1;
      continue;
    }
    const fromOurs = from !== null && own.has(from);
    const toOurs = to !== null && own.has(to);
    if (valueMovedAsTokens(r)) {
      const moves = opts.movesOf?.(r);
      const placements = moves ? placeMoves(moves, own) : [];
      if (placements.length > 0) {
        movementPlacedCount += 1;
        for (const placed of placements) {
          if (ruledOut.has(placed.counterparty)) {
            excludedCount += 1;
            continue;
          }
          if (pricedOnly && placed.usd === null) {
            droppedUnpricedCount += 1;
            continue;
          }
          record(placed.outbound, placed.counterparty, placed.usd, normalized(placed.chainId) ?? normalized(r.chain_id), txKey, txId);
        }
        continue;
      }
      if (opts.movesOf && moves === void 0) tokenValuePendingCount += 1;
      tokenValueCount += 1;
      tokenValueUsd += usd ?? 0;
      continue;
    }
    if (fromOurs && toOurs) {
      internalCount += 1;
      continue;
    }
    if (!fromOurs && !toOurs) {
      outsideEndsCount += 1;
      continue;
    }
    const outbound = fromOurs;
    const counterparty = outbound ? to : from;
    if (counterparty && ruledOut.has(counterparty)) {
      excludedCount += 1;
      continue;
    }
    if (pricedOnly && usd === null) {
      droppedUnpricedCount += 1;
      continue;
    }
    record(outbound, counterparty, usd, normalized(r.chain_id), txKey, txId);
  }
  const pinned = opts.pinnedCategories ?? [];
  const side = (tallies, unpriced, count2) => {
    const result = buildSide(tallies, unpriced, count2, pinned);
    for (const bucket of result.buckets) {
      bucket.transactionIds = bucket.transactionIds?.filter((id) => knownIds.has(id));
      if (!bucket.transactionIds?.length) delete bucket.transactionIds;
      for (const cp of bucket.top) {
        cp.transactionIds = cp.transactionIds?.filter((id) => knownIds.has(id));
        if (!cp.transactionIds?.length) delete cp.transactionIds;
      }
    }
    return result;
  };
  return {
    source: side(inflow, inUnpricedTxs.size, inTxs.size),
    destination: side(outflow, outUnpricedTxs.size, outTxs.size),
    internalCount,
    excludedCount,
    droppedUnpricedCount,
    outsideEndsCount,
    tokenValueCount,
    tokenValueUsd,
    tokenValuePendingCount,
    movementPlacedCount,
    passThroughCount: passThrough.size
  };
}

// src/funds/moves.ts
var VALUE_SCENE_KINDS = Object.freeze(["flow", "interaction"]);
var STRUCTURAL_EDGE_KINDS = /* @__PURE__ */ new Set(["call", "contains", "creates", "verify"]);
function num(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || !value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}
function lower(value) {
  return value ? value.toLowerCase() : null;
}
function partyOf(node, fallbackId) {
  return lower(node?.address) ?? fallbackId;
}
function assetOf(edge, txChain) {
  const a = edge.asset;
  if (!a) return null;
  const chainId = a.token_chain_id ?? edge.from_chain ?? txChain ?? null;
  const tokenAddress = lower(a.token_address);
  return {
    key: `${chainId ?? ""}:${tokenAddress ?? "native"}`,
    symbol: a.symbol ?? null,
    chainId,
    tokenAddress,
    decimals: a.decimals ?? null
  };
}
function amountOf(edge) {
  const a = edge.asset;
  if (!a) return null;
  const display = num(a.amount);
  if (display !== null) return display;
  const raw = num(a.amount_raw);
  if (raw === null || a.decimals == null) return null;
  return raw / 10 ** a.decimals;
}
function isObserved(edge) {
  return (edge.origin ?? "decoded") === "decoded" && !edge.virtual;
}
function isValueScene(scene) {
  return !!scene && VALUE_SCENE_KINDS.includes(String(scene.kind));
}
function unreadableMoves(scene, txChain) {
  if (!isValueScene(scene)) return 0;
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  let lost = 0;
  for (const edge of scene.edges) {
    if (!isObserved(edge) || STRUCTURAL_EDGE_KINDS.has(String(edge.kind))) continue;
    const a = edge.asset;
    if (!a) continue;
    if (partyOf(nodes.get(edge.source), edge.source) === partyOf(nodes.get(edge.target), edge.target)) continue;
    const amount = amountOf(edge);
    if (amount !== null && amount !== 0 && assetOf(edge, txChain)) continue;
    if (amount === 0) continue;
    if (a.amount == null && a.amount_raw == null) continue;
    lost += 1;
  }
  return lost;
}
function movesFromScene(scene, txId, txChain) {
  if (!isValueScene(scene)) return [];
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  const out = [];
  for (const edge of scene.edges) {
    if (!isObserved(edge) || STRUCTURAL_EDGE_KINDS.has(String(edge.kind))) continue;
    const asset = assetOf(edge, txChain);
    if (!asset) continue;
    const amount = amountOf(edge);
    if (amount === null || amount === 0) continue;
    const from = partyOf(nodes.get(edge.source), edge.source);
    const to = partyOf(nodes.get(edge.target), edge.target);
    if (from === to) continue;
    out.push({
      txId,
      chainId: edge.from_chain ?? nodes.get(edge.source)?.chain_id ?? txChain ?? null,
      from,
      to,
      asset,
      amount,
      usd: num(edge.asset?.usd)
    });
  }
  return out;
}
function chainOf(node, edgeChain) {
  return node?.chain_id ?? edgeChain ?? null;
}
var BRIDGE_EDGE_KINDS = /* @__PURE__ */ new Set(["bridge_out", "bridge_in", "cross_chain"]);
function bridgeNameFromScene(scene) {
  if (!scene) return null;
  for (const node of scene.nodes) {
    if (!node.off_chain) continue;
    if (String(node.kind ?? "").toLowerCase() !== "bridge") continue;
    return typeof node.label === "string" ? node.label.trim() : "";
  }
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  for (const edge of scene.edges) {
    if (!BRIDGE_EDGE_KINDS.has(String(edge.kind ?? "").toLowerCase())) continue;
    for (const end of [edge.target, edge.source]) {
      const label = nodes.get(end)?.label;
      if (typeof label === "string" && label.trim()) return label.trim();
    }
    return "";
  }
  return null;
}
function bridgeHopsFromScene(scene, txId, txChain) {
  if (!scene) return [];
  const local = txChain;
  const nodes = new Map(scene.nodes.map((n) => [n.id, n]));
  const isLocal = (chain) => !!chain && !!local && chain === local;
  const isRemote = (chain) => !!chain && (!local || chain !== local);
  const hops = /* @__PURE__ */ new Map();
  const add = (hop) => {
    const key2 = `${hop.direction}|${hop.localParty ?? ""}|${hop.remoteChain ?? ""}`;
    if (!hops.has(key2)) hops.set(key2, hop);
  };
  for (const bridge of scene.nodes) {
    if (!bridge.off_chain) continue;
    const into = scene.edges.filter((e) => e.target === bridge.id);
    const outOf = scene.edges.filter((e) => e.source === bridge.id);
    for (const e1 of into) {
      const srcNode = nodes.get(e1.source);
      const srcChain = chainOf(srcNode, e1.from_chain);
      for (const e2 of outOf) {
        const dstNode = nodes.get(e2.target);
        const dstChain = chainOf(dstNode, e2.to_chain);
        const valueEdge = e1.asset ? e1 : e2;
        const base = {
          txId,
          localChain: local,
          protocol: bridge.label || null,
          asset: assetOf(valueEdge, local),
          amount: amountOf(valueEdge),
          usd: num(valueEdge.asset?.usd)
        };
        if (isLocal(srcChain) && isRemote(dstChain)) {
          add({
            ...base,
            direction: "out",
            remoteChain: dstChain,
            localParty: srcNode?.address ? lower(srcNode.address) : null,
            remoteParty: lower(dstNode?.address),
            expected: !isObserved(e2)
          });
        } else if (isRemote(srcChain) && isLocal(dstChain)) {
          add({
            ...base,
            direction: "in",
            remoteChain: srcChain,
            localParty: dstNode?.address ? lower(dstNode.address) : null,
            remoteParty: lower(srcNode?.address),
            expected: !isObserved(e1)
          });
        }
      }
    }
  }
  for (const edge of scene.edges) {
    if (!edge.cross_chain) continue;
    const from = edge.from_chain ?? null;
    const to = edge.to_chain ?? null;
    const src = nodes.get(edge.source);
    const dst = nodes.get(edge.target);
    if (src?.off_chain || dst?.off_chain) continue;
    const base = {
      txId,
      localChain: local,
      protocol: null,
      asset: assetOf(edge, local),
      amount: amountOf(edge),
      usd: num(edge.asset?.usd),
      expected: !isObserved(edge)
    };
    if (isLocal(from) && isRemote(to)) {
      add({ ...base, direction: "out", remoteChain: to, localParty: lower(src?.address), remoteParty: lower(dst?.address) });
    } else if (isRemote(from) && isLocal(to)) {
      add({ ...base, direction: "in", remoteChain: from, localParty: lower(dst?.address), remoteParty: lower(src?.address) });
    }
  }
  return [...hops.values()];
}
function readTransactionMoney(input) {
  const moves = movesFromScene(input.valueScene, input.txId, input.chainId);
  let hops = bridgeHopsFromScene(input.valueScene, input.txId, input.chainId);
  if (hops.length === 0 && input.semanticScene) {
    hops = bridgeHopsFromScene(input.semanticScene, input.txId, input.chainId);
  }
  return {
    moves,
    hops,
    hasValueScene: isValueScene(input.valueScene),
    // A value scene can be present and still yield nothing — see
    // `unreadableMoves`. `hasValueScene` alone reads as "decoded, and it moved
    // nothing", which is a different claim.
    unreadable: unreadableMoves(input.valueScene, input.chainId)
  };
}
function focusMoves(moves, focus) {
  if (focus.size === 0 || moves.length === 0) return moves;
  const touching = moves.filter((move) => focus.has(move.from) || focus.has(move.to));
  return touching.length > 0 ? touching : moves;
}
function transitParties(legs, focus) {
  const out = /* @__PURE__ */ new Set();
  if (legs.length < 2 || focus.size === 0) return out;
  for (const leg of legs) {
    const far = leg.moves.filter((move) => focus.has(move.from) !== focus.has(move.to)).map((move) => focus.has(move.from) ? move.to : move.from);
    if (far.length === 0) return /* @__PURE__ */ new Set();
    for (const party of far) out.add(party);
  }
  return out;
}

// src/funds/stepFlow.ts
var PASS_THROUGH_TOLERANCE = 1e-3;
var EMPTY = Object.freeze({
  source: { buckets: [], usdTotal: 0, unpricedCount: 0, transactions: 0 },
  destination: { buckets: [], usdTotal: 0, unpricedCount: 0, transactions: 0 },
  assets: [],
  hops: [],
  passThrough: 0,
  transit: 0,
  moves: 0,
  transactions: 0,
  unpricedMoves: 0,
  unpricedQuotedElsewhere: 0,
  repriceable: []
});
function emptyStepFlow() {
  return EMPTY;
}
function stepFlow(moves, hops = [], options = {}) {
  if (moves.length === 0 && hops.length === 0) return EMPTY;
  const { categoryOf, attributionPending = false } = options;
  const balances = /* @__PURE__ */ new Map();
  const txs = /* @__PURE__ */ new Set();
  let unpricedMoves = 0;
  let counted = 0;
  const pricedByAsset = /* @__PURE__ */ new Map();
  const unpricedByAsset = /* @__PURE__ */ new Map();
  const unpricedTxsByAsset = /* @__PURE__ */ new Map();
  const touch = (party, move) => {
    const key2 = `${move.asset.key}|${party}`;
    let b = balances.get(key2);
    if (!b) {
      b = { party, chainId: move.chainId, asset: move.asset, in: 0, out: 0, usdIn: 0, usdOut: 0, unpriced: false, txs: /* @__PURE__ */ new Set() };
      balances.set(key2, b);
    }
    b.txs.add(move.txId);
    return b;
  };
  for (const move of moves) {
    if (move.amount === null || move.amount <= 0 || move.from === move.to) continue;
    counted += 1;
    txs.add(move.txId);
    if (move.usd === null) {
      unpricedMoves += 1;
      unpricedByAsset.set(move.asset.key, (unpricedByAsset.get(move.asset.key) ?? 0) + 1);
      let unpricedTxs = unpricedTxsByAsset.get(move.asset.key);
      if (!unpricedTxs) unpricedTxsByAsset.set(move.asset.key, unpricedTxs = /* @__PURE__ */ new Set());
      unpricedTxs.add(move.txId);
    } else {
      pricedByAsset.set(move.asset.key, (pricedByAsset.get(move.asset.key) ?? 0) + 1);
    }
    const from = touch(move.from, move);
    from.out += move.amount;
    from.usdOut += move.usd ?? 0;
    if (move.usd === null) from.unpriced = true;
    const to = touch(move.to, move);
    to.in += move.amount;
    to.usdIn += move.usd ?? 0;
    if (move.usd === null) to.unpriced = true;
  }
  const burnedIn = /* @__PURE__ */ new Set();
  const mintedIn = /* @__PURE__ */ new Set();
  for (const move of moves) {
    if (isZeroAddress(move.to)) burnedIn.add(move.txId);
    if (isZeroAddress(move.from)) mintedIn.add(move.txId);
  }
  const bridgeOut = /* @__PURE__ */ new Map();
  const bridgeIn = /* @__PURE__ */ new Map();
  const mark = (into, party, hop) => {
    const seen = into.get(party) ?? { chains: /* @__PURE__ */ new Set(), protocol: hop.protocol };
    if (hop.remoteChain) seen.chains.add(hop.remoteChain);
    seen.protocol ??= hop.protocol;
    into.set(party, seen);
  };
  for (const hop of hops) {
    const into = hop.direction === "out" ? bridgeOut : bridgeIn;
    const zeroMoved = hop.direction === "out" ? burnedIn.has(hop.txId) : mintedIn.has(hop.txId);
    if (zeroMoved) mark(into, ZERO_ADDRESS, hop);
    if (hop.localParty) mark(into, hop.localParty, hop);
  }
  const categoryFor = (party, chainId, outbound) => {
    if ((outbound ? bridgeOut : bridgeIn).has(party)) return "bridge";
    if (isZeroAddress(party)) return outbound ? "burned" : "minted";
    if (!party.startsWith("0x")) return "other";
    const resolved = categoryOf ? categoryOf(party, chainId) : null;
    return resolved ?? (attributionPending ? "resolving" : "unattributed");
  };
  const inflow = /* @__PURE__ */ new Map();
  const outflow = /* @__PURE__ */ new Map();
  const assetTotals = /* @__PURE__ */ new Map();
  const sourceTxs = /* @__PURE__ */ new Set();
  const destTxs = /* @__PURE__ */ new Set();
  let sourceUnpriced = 0;
  let destUnpriced = 0;
  const passThroughParties = /* @__PURE__ */ new Set();
  const transitSeen = /* @__PURE__ */ new Set();
  for (const b of balances.values()) {
    const { party } = b;
    if (options.transit?.has(party)) {
      transitSeen.add(party);
      continue;
    }
    const net = b.in - b.out;
    const gross = Math.max(b.in, b.out);
    if (b.in > 0 && b.out > 0 && Math.abs(net) <= gross * PASS_THROUGH_TOLERANCE) {
      passThroughParties.add(party);
      continue;
    }
    if (net === 0) continue;
    const isDestination = net > 0;
    const amount = Math.abs(net);
    const usdGross = isDestination ? b.usdIn : b.usdOut;
    const moved = isDestination ? b.in : b.out;
    const usd = b.unpriced ? null : moved > 0 ? usdGross * amount / moved : 0;
    const category = categoryFor(party, b.chainId, isDestination);
    const tallies = isDestination ? outflow : inflow;
    const tally = tallies.get(category) ?? {
      usd: 0,
      transactions: /* @__PURE__ */ new Set(),
      counterparties: /* @__PURE__ */ new Map(),
      assets: /* @__PURE__ */ new Map()
    };
    tally.usd += usd ?? 0;
    for (const t of b.txs) tally.transactions.add(t);
    addAsset(tally.assets, b.asset, amount, usd);
    const cp = tally.counterparties.get(party) ?? {
      address: party,
      usd: 0,
      transactions: /* @__PURE__ */ new Set(),
      assets: /* @__PURE__ */ new Map()
    };
    cp.usd += usd ?? 0;
    addCounterpartyChain(cp, b.chainId);
    for (const t of b.txs) cp.transactions.add(t);
    addAsset(cp.assets, b.asset, amount, usd);
    const bridge = (isDestination ? bridgeOut : bridgeIn).get(party);
    if (bridge) {
      cp.remoteChains ??= /* @__PURE__ */ new Set();
      for (const c of bridge.chains) cp.remoteChains.add(c);
      cp.protocol = bridge.protocol;
    }
    tally.counterparties.set(party, cp);
    tallies.set(category, tally);
    for (const t of b.txs) (isDestination ? destTxs : sourceTxs).add(t);
    if (usd === null) {
      if (isDestination) destUnpriced += 1;
      else sourceUnpriced += 1;
    }
    if (isDestination) {
      const total = assetTotals.get(b.asset.key) ?? { asset: b.asset, amount: 0, usd: 0 };
      total.amount += amount;
      total.usd = total.usd === null || usd === null ? null : total.usd + usd;
      assetTotals.set(b.asset.key, total);
    }
  }
  const pinned = options.pinnedCategories ?? [];
  const quoted = (asset) => (pricedByAsset.get(asset) ?? 0) > 0;
  const repriceable = /* @__PURE__ */ new Set();
  for (const [asset, ids] of unpricedTxsByAsset) if (quoted(asset)) for (const id of ids) repriceable.add(id);
  return {
    source: buildSide(inflow, sourceUnpriced, sourceTxs.size, pinned),
    destination: buildSide(outflow, destUnpriced, destTxs.size, pinned),
    assets: [...assetTotals.values()].sort((a, b) => (b.usd ?? -1) - (a.usd ?? -1) || b.amount - a.amount),
    hops: [...hops],
    passThrough: passThroughParties.size,
    transit: transitSeen.size,
    moves: counted,
    transactions: txs.size,
    unpricedMoves,
    unpricedQuotedElsewhere: [...unpricedByAsset].reduce((sum, [asset, count2]) => sum + (quoted(asset) ? count2 : 0), 0),
    repriceable: [...repriceable]
  };
}
function stepParties(moves) {
  const seen = /* @__PURE__ */ new Map();
  for (const move of moves) {
    for (const party of [move.from, move.to]) {
      if (!party.startsWith("0x") || isZeroAddress(party)) continue;
      const key2 = `${move.chainId ?? ""}::${party}`;
      if (!seen.has(key2)) seen.set(key2, { address: party, chainId: move.chainId });
    }
  }
  return [...seen.values()];
}

// src/funds/asserted.ts
var MAX_ASSERTED_AMOUNTS = 20;
var DECIMAL = /^\d+(\.\d+)?$/;
function isDecimalAmount(text) {
  return DECIMAL.test(text.trim());
}
function assertedValue(amount) {
  if (!isDecimalAmount(amount.amount)) return null;
  const n = Number(amount.amount.trim());
  return Number.isFinite(n) ? n : null;
}
function newAssertedAmount(id, direction = "in") {
  return { id, direction, amount: "", symbol: "", chain: "", source: "" };
}
function stringOr(value) {
  return asString(value) ?? "";
}
function parseAssertedAmounts(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const entry of raw.slice(0, MAX_ASSERTED_AMOUNTS)) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry;
    const direction = row.direction === "out" ? "out" : row.direction === "in" ? "in" : null;
    const id = stringOr(row.id).trim();
    if (!id || !direction) continue;
    out.push({
      id,
      direction,
      amount: stringOr(row.amount).trim(),
      symbol: stringOr(row.symbol).trim(),
      chain: stringOr(row.chain).trim(),
      source: stringOr(row.source).trim()
    });
  }
  return out;
}
function savableAssertedAmounts(amounts) {
  return amounts.filter((a) => isDecimalAmount(a.amount)).slice(0, MAX_ASSERTED_AMOUNTS);
}

// src/funds/compare.ts
function symbolKey(symbol) {
  const s = (symbol ?? "").trim().toUpperCase();
  return s || null;
}
function amountsBySymbol(step) {
  const out = /* @__PURE__ */ new Map();
  for (const total of step.observed) {
    const key2 = symbolKey(total.asset.symbol);
    if (!key2 || total.amount <= 0) continue;
    const seen = out.get(key2);
    out.set(key2, { amount: (seen?.amount ?? 0) + total.amount, basis: "observed" });
  }
  const asserted = /* @__PURE__ */ new Map();
  for (const a of step.asserted ?? []) {
    const key2 = symbolKey(a.symbol);
    const value = assertedValue(a);
    if (!key2 || value === null || value <= 0) continue;
    const seen = asserted.get(key2) ?? { in: 0, out: 0 };
    seen[a.direction] += value;
    asserted.set(key2, seen);
  }
  for (const [key2, sums] of asserted) {
    if (out.has(key2)) continue;
    out.set(key2, { amount: Math.max(sums.in, sums.out), basis: "asserted" });
  }
  return out;
}
function compareSteps(steps) {
  const bySymbol = steps.map(amountsBySymbol);
  const out = /* @__PURE__ */ new Map();
  steps.forEach((step, index) => {
    const mine = bySymbol[index];
    const comparisons = [];
    for (const [symbol, { amount, basis }] of mine) {
      for (let j = index - 1; j >= 0; j -= 1) {
        const earlier = bySymbol[j].get(symbol);
        if (!earlier || earlier.amount <= 0) continue;
        comparisons.push({
          symbol,
          amount,
          basis,
          ratio: amount / earlier.amount,
          against: { id: steps[j].id, index: j, amount: earlier.amount, basis: earlier.basis }
        });
        break;
      }
    }
    if (comparisons.length > 0) out.set(step.id, comparisons);
  });
  return out;
}
function isNotableRatio(ratio) {
  return ratio > 1.01 || ratio < 0.99;
}
var COMPACT = new Intl.NumberFormat("en-US", { notation: "compact", maximumSignificantDigits: 2 });
function formatRatio(ratio) {
  if (!Number.isFinite(ratio)) return "\xD7\u2014";
  if (ratio >= 1e4) return `\xD7${COMPACT.format(ratio)}`;
  if (ratio >= 100) return `\xD7${Math.round(ratio).toLocaleString()}`;
  if (ratio >= 10) return `\xD7${ratio.toFixed(1)}`;
  if (ratio >= 0.1) return `\xD7${ratio.toFixed(2)}`;
  if (ratio >= 0.01 || ratio <= 0) return `\xD7${ratio.toPrecision(2)}`;
  return `\xF7${COMPACT.format(1 / ratio)}`;
}

// src/funds/phases.ts
function key(label) {
  return label.trim().toLowerCase();
}
function groupPhases(items, phaseOf) {
  const groups = /* @__PURE__ */ new Map();
  items.forEach((item, index) => {
    const label = (phaseOf(item) ?? "").trim();
    if (!label) return;
    const k = key(label);
    const group = groups.get(k) ?? { label, items: [], indices: [] };
    group.items.push(item);
    group.indices.push(index);
    groups.set(k, group);
  });
  return [...groups.values()];
}
function phaseLabels(items, phaseOf) {
  return groupPhases(items, phaseOf).map((g) => g.label);
}

// src/funds/format.ts
var COMPACT2 = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 });
var PLAIN = new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 });
var SMALL = new Intl.NumberFormat("en-US", { maximumSignificantDigits: 4 });
var MIN_DISPLAY_AMOUNT = 1e-4;
function formatAssetAmount(amount) {
  if (!Number.isFinite(amount)) return "\u2014";
  if (amount === 0) return "0";
  const abs = Math.abs(amount);
  if (abs < MIN_DISPLAY_AMOUNT) return amount < 0 ? `>-${MIN_DISPLAY_AMOUNT}` : `<${MIN_DISPLAY_AMOUNT}`;
  if (abs >= 1e6) return COMPACT2.format(amount);
  if (abs >= 1) return PLAIN.format(amount);
  return SMALL.format(amount);
}
function assetSymbol(asset) {
  if (asset.symbol) return asset.symbol;
  if (asset.tokenAddress) return shortenAddress(asset.tokenAddress);
  return "native";
}
function ambiguousAssetSymbols(assets) {
  const bySymbol = /* @__PURE__ */ new Map();
  for (const { asset } of assets) {
    const symbol = assetSymbol(asset);
    const keys = bySymbol.get(symbol) ?? /* @__PURE__ */ new Set();
    keys.add(asset.key);
    bySymbol.set(symbol, keys);
  }
  const ambiguous = /* @__PURE__ */ new Set();
  for (const [symbol, keys] of bySymbol) if (keys.size > 1) ambiguous.add(symbol);
  return ambiguous;
}
function formatAssetAmountWithSymbol(amount) {
  return `${formatAssetAmount(amount.amount)} ${assetSymbol(amount.asset)}`;
}

// src/metadata-analytics/protocol-names.generated.json
var protocol_names_generated_default = {
  categories: {
    aave_v3: "lending",
    account_closure: "infrastructure",
    across: "bridge",
    address_lookup_table: "infrastructure",
    aerodrome: "exchange",
    aldrin: "exchange",
    aldrin_v2: "exchange",
    alphaq: "exchange",
    angstrom: "exchange",
    aquifer: "exchange",
    arbitrage_bot_foaft: "mev",
    arbos: "infrastructure",
    archer: "exchange",
    associated_token_account: "erc20",
    babylon_staking: "staking",
    banana_gun: "mev",
    binaryfi: "exchange",
    bisonfi: "exchange",
    bisonfi_predict: "exchange",
    bitcoin_stamps: "nft",
    bonkswap: "exchange",
    boop_fun: "exchange",
    bpf_loader_upgradeable: "infrastructure",
    brc20: "erc20",
    byreal: "exchange",
    carrot: "staking",
    cctp: "bridge",
    chainflip: "bridge",
    chainlink_automation: "oracle",
    chainlink_ccip: "bridge",
    chainlink_direct_request: "oracle",
    compute_budget: "infrastructure",
    conditional_tokens: "derivatives",
    coredao: "staking",
    counterparty: "exchange",
    cowswap: "exchange",
    crema: "exchange",
    cropper: "exchange",
    cryptopunks: "nft",
    defituna: "exchange",
    denali: "exchange",
    deriverse: "exchange",
    dexlab: "exchange",
    dflow_aggregator_v4: "aggregator",
    ed25519_program: "infrastructure",
    ens: "nft",
    erc1155: "nft",
    erc20: "erc20",
    erc4337: "account_abstraction",
    erc721: "nft",
    flint: "exchange",
    flux: "exchange",
    fluxbeam: "exchange",
    gatorswap: "exchange",
    gavel: "exchange",
    generic: "other",
    gnosis_safe: "safe",
    goonfi_v2: "exchange",
    goosefx_gamma: "exchange",
    guacswap: "exchange",
    hadron: "exchange",
    heaven: "exchange",
    helium_treasury: "staking",
    huma: "staking",
    humidifi: "exchange",
    hylo_exchange: "staking",
    hylo_stability_pool: "staking",
    hyperlane: "bridge",
    hyperliquid_account: "account_abstraction",
    hyperliquid_bridge: "bridge",
    hyperliquid_bridge2: "bridge",
    hyperliquid_evm: "infrastructure",
    hyperliquid_staking: "staking",
    hyperliquid_trading: "derivatives",
    hyperliquid_transfer: "exchange",
    hyperliquid_validator: "infrastructure",
    hyperliquid_vault: "derivatives",
    invariant: "exchange",
    jaredfromsubway: "mev",
    jupiter_dca: "exchange",
    jupiter_lend_earn: "lending",
    jupiter_limit_order: "exchange",
    jupiter_limit_order_v2: "exchange",
    jupiter_perps: "derivatives",
    jupiter_rfq_v2: "exchange",
    jupiter_v4: "aggregator",
    jupiter_v6: "aggregator",
    juplend_amm: "exchange",
    kipseli: "exchange",
    kyberswap_meta_aggregator: "aggregator",
    layerzero_oft: "bridge",
    layerzero_v2: "bridge",
    lemmingsfi: "exchange",
    lifi: "aggregator",
    lifinity_v2: "exchange",
    lighthouse: "infrastructure",
    m_swap: "exchange",
    magic_eden_v2: "nft",
    manifest: "exchange",
    marinade: "staking",
    mayan: "bridge",
    mercurial: "exchange",
    metadao: "exchange",
    metamask_bridge: "aggregator",
    metaplex_bubblegum: "nft",
    metaplex_core: "nft",
    metaplex_token_metadata: "nft",
    meteora_damm_v2: "exchange",
    meteora_dbc: "exchange",
    meteora_dlmm: "exchange",
    meteora_pools: "exchange",
    metric: "exchange",
    mev_bot: "mev",
    monero_ringct: "privacy",
    moonit: "exchange",
    multicall: "aggregator",
    native_transfer: "erc20",
    near_intents: "bridge",
    obric_v2: "exchange",
    obsidian: "exchange",
    okx_dex_router: "aggregator",
    okx_xbridge: "aggregator",
    omni_layer: "erc20",
    omnipair: "exchange",
    one_dex: "exchange",
    oneinch: "aggregator",
    op_return_data: "other",
    op_stack_l1_block: "infrastructure",
    openbook_v2: "exchange",
    orca_v1: "exchange",
    orca_v2: "exchange",
    orca_whirlpool: "exchange",
    ordinals: "nft",
    pancakeswap_factory: "exchange",
    pancakeswap_router: "exchange",
    pancakeswap_solana: "exchange",
    pancakeswap_v3_pool: "exchange",
    penguin: "exchange",
    perena: "exchange",
    perena_star_v2: "staking",
    phoenix: "exchange",
    phoenix_eternal: "derivatives",
    polymarket_ctf_exchange: "derivatives",
    polymarket_neg_risk: "derivatives",
    pons_v2_curve: "exchange",
    pons_v2_factory: "exchange",
    privacy_pools: "privacy",
    program_metadata: "infrastructure",
    pumpfun: "exchange",
    pumpfun_fee: "exchange",
    pumpswap_amm: "exchange",
    quantum: "exchange",
    quay: "exchange",
    railgun: "privacy",
    raydium_amm_v4: "exchange",
    raydium_clmm: "exchange",
    raydium_cpmm: "exchange",
    raydium_launchlab: "exchange",
    relay: "bridge",
    riptide: "exchange",
    rootstock: "bridge",
    runes: "erc20",
    runner_rodeo: "exchange",
    saber: "exchange",
    saber_decimals: "exchange",
    sanctum_infinity: "staking",
    sanctum_multi_validator: "staking",
    sanctum_prop_s: "staking",
    sanctum_router: "staking",
    sanctum_single_validator: "staking",
    sanctum_sols: "staking",
    sanctum_spl_stake_pool: "staking",
    saros: "exchange",
    saros_dlmm: "exchange",
    scale_amm: "exchange",
    scale_vmm: "exchange",
    scorch: "exchange",
    scorch_2: "exchange",
    seaport: "nft",
    secp256k1_program: "infrastructure",
    solayer: "staking",
    solfi: "exchange",
    solfi_v2: "exchange",
    spl_memo_v1: "infrastructure",
    spl_memo_v2: "infrastructure",
    spl_token: "erc20",
    spl_token_2022: "erc20",
    spl_token_swap: "exchange",
    stabble_clmm: "exchange",
    stabble_stable_swap: "exchange",
    stabble_weighted_swap: "exchange",
    stableswap: "exchange",
    stacks: "infrastructure",
    stake: "infrastructure",
    stargate: "bridge",
    stepn: "exchange",
    sushiswap_router: "exchange",
    sushiswap_v2_factory: "exchange",
    system: "infrastructure",
    taurusfi: "exchange",
    tensor_swap: "nft",
    tessera_v: "exchange",
    thorchain: "bridge",
    thorchain_memo: "bridge",
    tokemak_messaging: "bridge",
    tornado_cash: "privacy",
    tornado_cash_nova: "privacy",
    trench: "exchange",
    trends: "exchange",
    uniswap_permit2: "exchange",
    uniswap_universal_router: "exchange",
    uniswap_v1_exchange: "exchange",
    uniswap_v1_factory: "exchange",
    uniswap_v2_factory: "exchange",
    uniswap_v2_pair: "exchange",
    uniswap_v2_router: "exchange",
    uniswap_v3_factory: "exchange",
    uniswap_v3_pool: "exchange",
    uniswap_v3_position_manager: "exchange",
    uniswap_v3_router: "exchange",
    uniswap_v4: "exchange",
    uniswap_v4_position_manager: "exchange",
    usdt0: "bridge",
    vault_liquid_unstake: "staking",
    virtuals: "exchange",
    voltr: "staking",
    vote: "infrastructure",
    whalestreet: "exchange",
    wintermute: "mev",
    woofi: "exchange",
    wormhole: "bridge",
    xorca: "staking",
    zcash_shielded: "privacy",
    zeroex: "exchange",
    zerofi: "exchange",
    zkbob: "privacy"
  },
  names: {
    aave_v3: "Aave V3",
    account_closure: "Account closure",
    across: "Across Protocol",
    address_lookup_table: "Address Lookup Table",
    aerodrome: "Aerodrome / Velodrome",
    aldrin: "Aldrin",
    aldrin_v2: "Aldrin v2",
    alphaq: "AlphaQ",
    angstrom: "Angstrom",
    aquifer: "Aquifer",
    arbitrage_bot_foaft: "Arbitrage Bot (FoaFt)",
    arbos: "ArbOS",
    archer: "Archer",
    associated_token_account: "Associated Token Account",
    babylon_staking: "Babylon",
    banana_gun: "Banana Gun",
    binaryfi: "BinaryFi",
    bisonfi: "BisonFi",
    bisonfi_predict: "BisonFi Predict",
    bitcoin_stamps: "Bitcoin Stamps",
    bonkswap: "Bonkswap",
    boop_fun: "Boop.fun",
    bpf_loader_upgradeable: "BPF Upgradeable Loader",
    brc20: "BRC-20",
    byreal: "Byreal",
    carrot: "Carrot",
    cctp: "Circle CCTP",
    chainflip: "Chainflip",
    chainlink_automation: "Chainlink Automation",
    chainlink_ccip: "Chainlink CCIP",
    chainlink_direct_request: "Chainlink Direct Request",
    compute_budget: "Compute Budget",
    conditional_tokens: "Conditional Tokens",
    coredao: "Core DAO",
    counterparty: "Counterparty",
    cowswap: "CoW Swap",
    crema: "Crema",
    cropper: "Cropper",
    cryptopunks: "CryptoPunks",
    defituna: "DefiTuna",
    denali: "Denali",
    deriverse: "Deriverse",
    dexlab: "DexLab",
    dflow_aggregator_v4: "DFlow",
    ed25519_program: "Ed25519 Signature Verify",
    ens: "ENS",
    erc1155: "ERC1155",
    erc20: "ERC20",
    erc4337: "ERC-4337",
    erc721: "ERC721",
    flint: "Flint",
    flux: "Flux",
    fluxbeam: "FluxBeam",
    gatorswap: "GatorSwap",
    gavel: "Gavel",
    generic: "Generic",
    gnosis_safe: "Gnosis Safe",
    goonfi_v2: "GoonFi v2",
    goosefx_gamma: "GooseFX Gamma",
    guacswap: "Guacswap",
    hadron: "Hadron",
    heaven: "Heaven",
    helium_treasury: "Helium Treasury Management",
    huma: "Huma",
    humidifi: "HumidiFi",
    hylo_exchange: "Hylo Exchange",
    hylo_stability_pool: "Hylo Stability Pool",
    hyperlane: "Hyperlane",
    hyperliquid_account: "Hyperliquid account",
    hyperliquid_bridge: "Hyperliquid bridge",
    hyperliquid_bridge2: "Hyperliquid Bridge2",
    hyperliquid_evm: "HyperEVM via HyperCore",
    hyperliquid_staking: "Hyperliquid staking",
    hyperliquid_trading: "Hyperliquid trading",
    hyperliquid_transfer: "Hyperliquid transfers",
    hyperliquid_validator: "Hyperliquid validators and deployers",
    hyperliquid_vault: "Hyperliquid vaults",
    invariant: "Invariant",
    jaredfromsubway: "jaredfromsubway",
    jupiter_dca: "Jupiter DCA",
    jupiter_lend_earn: "Jupiter Lend Earn",
    jupiter_limit_order: "Jupiter Limit Order",
    jupiter_limit_order_v2: "Jupiter Limit Order v2",
    jupiter_perps: "Jupiter Perps",
    jupiter_rfq_v2: "Jupiter RFQ",
    jupiter_v4: "Jupiter v4",
    jupiter_v6: "Jupiter",
    juplend_amm: "JupLend AMM",
    kipseli: "Kipseli",
    kyberswap_meta_aggregator: "KyberSwap",
    layerzero_oft: "LayerZero OFT",
    layerzero_v2: "LayerZero V2",
    lemmingsfi: "LemmingsFi",
    lifi: "LI.FI",
    lifinity_v2: "Lifinity v2",
    lighthouse: "Lighthouse",
    m_swap: "M Swap",
    magic_eden_v2: "Magic Eden v2",
    manifest: "Manifest",
    marinade: "Marinade Finance",
    mayan: "Mayan",
    mercurial: "Mercurial",
    metadao: "MetaDAO",
    metamask_bridge: "MetaMask Bridge",
    metaplex_bubblegum: "Metaplex Bubblegum",
    metaplex_core: "Metaplex Core",
    metaplex_token_metadata: "Metaplex Token Metadata",
    meteora_damm_v2: "Meteora DAMM v2",
    meteora_dbc: "Meteora Dynamic Bonding Curve",
    meteora_dlmm: "Meteora DLMM",
    meteora_pools: "Meteora Pools",
    metric: "Metric",
    mev_bot: "MEV Bot",
    monero_ringct: "Monero RingCT",
    moonit: "Moonit",
    multicall: "Multicall",
    native_transfer: "Native transfer",
    near_intents: "NEAR Intents",
    obric_v2: "Obric v2",
    obsidian: "Obsidian",
    okx_dex_router: "OKX DEX Router",
    okx_xbridge: "OKX XBridge",
    omni_layer: "Omni Layer",
    omnipair: "Omnipair",
    one_dex: "1DEX",
    oneinch: "1inch",
    op_return_data: "OP_RETURN data",
    op_stack_l1_block: "OP Stack L1Block",
    openbook_v2: "OpenBook v2",
    orca_v1: "Orca v1",
    orca_v2: "Orca v2",
    orca_whirlpool: "Orca Whirlpool",
    ordinals: "Ordinals",
    pancakeswap_factory: "PancakeSwap",
    pancakeswap_router: "PancakeSwap",
    pancakeswap_solana: "PancakeSwap",
    pancakeswap_v3_pool: "PancakeSwap",
    penguin: "Penguin",
    perena: "Perena",
    perena_star_v2: "Perena Star v2",
    phoenix: "Phoenix",
    phoenix_eternal: "Phoenix Eternal",
    polymarket_ctf_exchange: "Polymarket CTF Exchange",
    polymarket_neg_risk: "Polymarket Neg Risk",
    pons_v2_curve: "Pons V2 curve",
    pons_v2_factory: "Pons V2",
    privacy_pools: "Privacy Pools / 0xbow",
    program_metadata: "Program Metadata",
    pumpfun: "pump.fun",
    pumpfun_fee: "pump.fun fees",
    pumpswap_amm: "PumpSwap",
    quantum: "Quantum",
    quay: "Quay",
    railgun: "RAILGUN",
    raydium_amm_v4: "Raydium AMM v4",
    raydium_clmm: "Raydium CLMM",
    raydium_cpmm: "Raydium CPMM",
    raydium_launchlab: "Raydium LaunchLab",
    relay: "Relay",
    riptide: "Riptide",
    rootstock: "Rootstock",
    runes: "Runes",
    runner_rodeo: "RunnerRodeo",
    saber: "Saber",
    saber_decimals: "Saber (Decimals)",
    sanctum_infinity: "Sanctum Infinity",
    sanctum_multi_validator: "Sanctum Multi-Validator Pool",
    sanctum_prop_s: "Sanctum Prop S",
    sanctum_router: "Sanctum Router",
    sanctum_single_validator: "Sanctum Single-Validator Pool",
    sanctum_sols: "Sanctum SOLS",
    sanctum_spl_stake_pool: "Sanctum SPL Stake Pool",
    saros: "Saros",
    saros_dlmm: "Saros DLMM",
    scale_amm: "Scale AMM",
    scale_vmm: "Scale VMM",
    scorch: "Scorch",
    scorch_2: "Scorch",
    seaport: "Seaport",
    secp256k1_program: "Secp256k1 Signature Verify",
    solayer: "Solayer",
    solfi: "SolFi",
    solfi_v2: "SolFi v2",
    spl_memo_v1: "Memo v1",
    spl_memo_v2: "Memo",
    spl_token: "SPL Token",
    spl_token_2022: "Token-2022",
    spl_token_swap: "SPL Token Swap",
    stabble_clmm: "Stabble CLMM",
    stabble_stable_swap: "Stabble Stable Swap",
    stabble_weighted_swap: "Stabble Weighted Swap",
    stableswap: "Stableswap",
    stacks: "Stacks",
    stake: "Stake Program",
    stargate: "Stargate Finance",
    stepn: "StepN",
    sushiswap_router: "SushiSwap",
    sushiswap_v2_factory: "SushiSwap",
    system: "System Program",
    taurusfi: "TaurusFi",
    tensor_swap: "Tensor Swap",
    tessera_v: "TesseraV",
    thorchain: "THORChain",
    thorchain_memo: "THORChain-style memo",
    tokemak_messaging: "Tokemak Cross-Chain Messaging",
    tornado_cash: "Tornado Cash",
    tornado_cash_nova: "Tornado Cash Nova",
    trench: "Trench",
    trends: "Trends",
    uniswap_permit2: "Permit2",
    uniswap_universal_router: "Uniswap Universal Router",
    uniswap_v1_exchange: "Uniswap V1",
    uniswap_v1_factory: "Uniswap V1",
    uniswap_v2_factory: "Uniswap V2",
    uniswap_v2_pair: "Uniswap V2",
    uniswap_v2_router: "Uniswap V2",
    uniswap_v3_factory: "Uniswap V3",
    uniswap_v3_pool: "Uniswap V3",
    uniswap_v3_position_manager: "Uniswap V3 Positions",
    uniswap_v3_router: "Uniswap V3",
    uniswap_v4: "Uniswap V4",
    uniswap_v4_position_manager: "Uniswap V4 Positions",
    usdt0: "USDT0 / Legacy Mesh",
    vault_liquid_unstake: "VaultLiquidUnstake",
    virtuals: "Virtuals",
    voltr: "Voltr",
    vote: "Vote Program",
    whalestreet: "WhaleStreet",
    wintermute: "Wintermute Market Maker",
    woofi: "WOOFi",
    wormhole: "Wormhole",
    xorca: "xORCA",
    zcash_shielded: "Zcash shielded pools",
    zeroex: "0x",
    zerofi: "ZeroFi",
    zkbob: "zkBob"
  }
};

// src/metadata-analytics/protocolNames.ts
var PROTOCOL_CATALOG_CATEGORIES = [
  "bridge",
  "exchange",
  "aggregator",
  "lending",
  "flashloan",
  "derivatives",
  "safe",
  "account_abstraction",
  "governance",
  "oracle",
  "infrastructure",
  "erc20",
  "nft",
  "staking",
  "mev",
  "exploit",
  "privacy",
  "other"
];
var SnapshotSchema = z.object({
  names: z.record(z.string(), z.string()).default({}),
  categories: z.record(z.string(), z.string()).default({})
});
var SNAPSHOT = SnapshotSchema.parse(protocol_names_generated_default);
var NAMES = Object.freeze(SNAPSHOT.names);
Object.freeze(SNAPSHOT.categories);
new Set(PROTOCOL_CATALOG_CATEGORIES);
function protocolDisplayName(protocolId) {
  if (!protocolId) return void 0;
  const name = NAMES[protocolId];
  return name && name.trim() ? name : void 0;
}

// src/grouping/sagas.ts
var SAGA_LABELS = {
  "bridge.ccip": "Chainlink CCIP",
  "bridge.cctp": "Circle CCTP",
  "bridge.wormhole": "Wormhole",
  // The rest of the bundled bridges, which fell through to the generic
  // prettify and so introduced themselves as "Bridge Layerzero V2" and
  // "Bridge Ibc" — the namespace leaked into the name and the protocol's own
  // capitalisation was lost. A card naming a protocol should spell it the way
  // the protocol does.
  //
  // Both LayerZero generations are listed and both say which: they are
  // different rails with different contracts and different chain numbering,
  // and a group card that called them both "LayerZero" would leave a reader
  // unable to tell which index to go and look in.
  "bridge.layerzero_v1": "LayerZero V1",
  "bridge.layerzero_v2": "LayerZero V2",
  "bridge.across_v3": "Across",
  "bridge.hyperlane": "Hyperlane",
  "bridge.mayan": "Mayan",
  // The one saga in this list whose key is not on the chain. A Relay fill is a
  // solver paying out of its own capital and identifies itself to nobody, so the
  // two legs are joined by the request id Relay's own API mints — carried as a
  // `CrossChainRef` with `off_chain` provenance. Nothing else here needs to know
  // that; the label is the only Relay-specific line in this package.
  "bridge.relay": "Relay",
  "bridge.thorchain": "THORChain",
  "bridge.chainflip": "Chainflip",
  "bridge.hyperliquid": "Hyperliquid bridge",
  "bridge.hop": "Hop",
  "bridge.ibc": "IBC",
  "trade.token_position": "Trading position",
  "token.launch": "Token launch",
  "pool.lifecycle": "Pool lifecycle",
  // A prediction market's whole story, keyed on its `conditionId`. "Prediction
  // market" rather than "Conditional tokens" — the latter is the name of the
  // contract standard, and a reader looking at a group of bets on a question
  // should not have to know it.
  "market.lifecycle": "Prediction market",
  // The ELECTION, not one of its questions. A multi-outcome ("neg risk") market
  // is N binary conditions, each already titled "Prediction market" — naming
  // this one the same would put eleven identically-titled groups on a page and
  // leave the reader to work out that one of them is the other ten.
  "negrisk.market": "Multi-outcome market",
  "pool.rug": "Rug pull",
  "pool.jit_front_run": "JIT front-run",
  "mev.sandwich": "Sandwich",
  "launch.insider_buy": "Insider buy",
  // Custody. "Exchange transfers" rather than "Exchange deposits" because the
  // group holds both directions — the saga kind names the common case, the
  // group does not.
  "flow.exchange_deposit": "Exchange transfers",
  // One CoW order's placement and the settlement that filled it, keyed on the
  // derived `orderUid`. Prettified it read "Exchange Cowswap Order".
  "exchange.cowswap_order": "CoW Swap order",
  // NOT "Peel chain", which is the technique's name among investigators and
  // means nothing to anybody else. What the finding says is that money moved
  // through a series of wallets and stopped somewhere it can no longer be
  // followed.
  //
  // "somewhere" rather than "an exchange" because the detector's terminus is a
  // CLASS now — an exchange, a custodial service, a bridge escrow, a mixer, a
  // sanctioned address. A static label naming one of them would be wrong for
  // the other four; `peelChainLabel` below narrows it when the group's payload
  // says which.
  "flow.peel_chain": "Funds forwarded through intermediaries",
  // NOT "Dust campaign", for the same reason the peel chain is not called one:
  // "dusting" is the technique's name among investigators and says nothing to
  // the person whose wallet is full of it. What the finding says is that one
  // address sprayed worthless transfers at a great many wallets.
  "spam.dust_campaign": "Bulk unsolicited transfers",
  approve_spend: "Approve / spend",
  userop_retry: "User-op retry",
  // Every transaction touching one catalog entity — a set of wallets someone
  // labelled. The served group name is "Entity activity · <entity name>"
  // (`commons.grouping.naming`), and a commons test fails if this map and that
  // one spell a family differently.
  "entity.flow": "Entity activity"
};
function prettifyToken(value) {
  const words = value.replace(/[._-]+/g, " ").trim().split(/\s+/).filter(Boolean);
  return words.length === 0 ? value : words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
function sagaLabel(kind) {
  if (!kind) return "Cross-chain";
  const listed = SAGA_LABELS[kind];
  if (listed) return listed;
  if (kind.startsWith("bridge.")) {
    const named = protocolDisplayName(kind.slice("bridge.".length));
    if (named) return named;
  }
  return prettifyToken(kind);
}
function titleRole(role) {
  return role.length ? role.charAt(0).toUpperCase() + role.slice(1) : role;
}

// src/funds/transfers.ts
function sagaKeyParts(key2) {
  const at = key2.indexOf(":");
  return at < 0 ? { sagaKind: "", value: key2 } : { sagaKind: key2.slice(0, at), value: key2.slice(at + 1) };
}
function citedTransferOf(key2, saga) {
  const { sagaKind, value } = sagaKeyParts(key2);
  const label = `${sagaLabel(saga?.sagaKind ?? sagaKind)} \xB7 ${shortenIdentifier(saga?.value ?? value)}`;
  if (!saga) {
    return { key: key2, label, legsObserved: 0, legsExpected: 0, state: "not in this case's rows", complete: false };
  }
  const observed = Math.max(saga.legs.length, saga.memberCount ?? 0);
  const expected = Math.max(saga.expectedRoles.length, observed);
  const state = saga.complete ? "complete" : saga.missingRoles.length > 0 ? `awaiting ${saga.missingRoles.map((r) => titleRole(r).toLowerCase()).join(" + ")}` : saga.openEnded ? "open" : "incomplete";
  return { key: key2, label, legsObserved: observed, legsExpected: expected, state, complete: saga.complete };
}

// src/funds/pricing.ts
var NO_SCENE_PRICING = Object.freeze({
  valueEdges: 0,
  pricedEdges: 0,
  tokensQuoted: 0,
  priced: false
});
function mergeScenePricing(entries) {
  let valueEdges = 0;
  let pricedEdges = 0;
  let tokensQuoted = 0;
  let priced = false;
  let known = 0;
  let quotesMissing = 0;
  for (const entry of entries) {
    if (!entry) continue;
    known += 1;
    valueEdges += entry.valueEdges;
    pricedEdges += entry.pricedEdges;
    tokensQuoted += entry.tokensQuoted;
    quotesMissing += entry.quotesMissing ?? 0;
    priced = priced || entry.priced;
  }
  return { valueEdges, pricedEdges, tokensQuoted, priced, known, quotesMissing };
}
function unpricedReason(pricing, unpricedMoves, quotedElsewhere = 0) {
  if (unpricedMoves <= 0) return null;
  const moves = `${unpricedMoves} movement${unpricedMoves === 1 ? "" : "s"}`;
  const missed = Math.min(Math.max(quotedElsewhere, 0), unpricedMoves);
  if (missed > 0 && pricing?.priced) {
    const rest = unpricedMoves - missed;
    const one = missed === 1;
    const lead = !rest ? `${moves} unpriced although the same asset is priced elsewhere in this set` : `${moves} unpriced \u2014 ${missed} of them in an asset priced elsewhere in this set`;
    return `${lead}: the price lookup for ${one ? "that transaction" : "those transactions"} found no quote at ${one ? "its" : "their"} time. A re-run asks the price sources again.` + (rest > 0 ? ` The other ${rest} have no quote for the asset.` : "");
  }
  if (!pricing || pricing.known === 0) {
    return `${moves} unpriced \u2014 in the amounts, not in USD.`;
  }
  if (!pricing.priced) {
    return `${moves} unpriced: the run that processed them did no pricing. Re-run on a fuller pipeline to put dollars on these amounts.`;
  }
  return `${moves} unpriced \u2014 no quote for the asset at that time, so the amount is all there is.`;
}

export { ALARMING_CATEGORIES, CATEGORY_LABEL, EMPTY_SIDE, MAX_ASSERTED_AMOUNTS, MAX_BUCKET_COUNTERPARTIES, MIN_DISPLAY_AMOUNT, NOTABLE_CATEGORIES, NO_SCENE_PRICING, PASS_THROUGH_TOLERANCE, UNKNOWN_CATEGORIES, VALUE_SCENE_KINDS, ZERO_ADDRESS, addAsset, ambiguousAssetSymbols, amountsBySymbol, assertedValue, assetSymbol, bridgeHopsFromScene, bridgeNameFromScene, buildSide, categoryLabel, citedTransferOf, compareSteps, emptyStepFlow, flowSelectionIds, focusMoves, formatAssetAmount, formatAssetAmountWithSymbol, formatRatio, fundsFlow, groupPhases, isDecimalAmount, isNotableRatio, isValueScene, isZeroAddress, mergeScenePricing, movesFromScene, newAssertedAmount, parseAssertedAmounts, phaseLabels, readTransactionMoney, rowPricedUsd, rowValueMovedAsTokens, sagaKeyParts, savableAssertedAmounts, stepFlow, stepParties, subjectFlowOf, transitParties, unpricedReason, unreadableMoves };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map